/* ==========================================================================
   Nordenheim — plate-snap section
   --------------------------------------------------------------------------
   A custom element so the theme editor is handled for free: connectedCallback
   fires whenever the element enters the document (including when the editor
   re-renders the section) and disconnectedCallback fires when the old subtree
   is removed, so timers and observers are torn down without any
   shopify:section:* listeners.

   All state lives on the instance and every query is scoped to `this`, so two
   instances on one page never interfere.

   Data comes from a <script type="application/json"> child. In production that
   is emitted by Liquid from collections/products/metafields; the local preview
   harness emits the identical shape, so this file is never aware of Shopify.
   ========================================================================== */

/** "1 245 kr" -> 1245. The store's money strings carry spaces (and sometimes a
    non-breaking one) as the thousands separator; everything that is not a digit
    goes. Decimals are not a case here — every price on this section is whole
    kronor — and treating a stray decimal as digits would inflate the number, so
    anything after a comma or dot is dropped first. */
function nhMoneyNum(str) {
  // ROUND 22.1 — the English storefront writes "1,245 kr": the comma is a
  // THOUSANDS separator, not a decimal point, and cutting at it read the
  // Collector as 1 kr — which silenced the nudge at the Trio and priced a
  // five-plate tray at "1 kr" on the button. The rule that survives every
  // format Shopify can emit: the LAST separator is a decimal point only when
  // exactly two digits follow it ("1.245,00", "1,245.00"); three digits after
  // it ("1,245") is a thousands group; no separator is just the amount.
  const raw = String(str == null ? '' : str).trim();
  const m = raw.match(/^(.*)[.,](\d{2})(?!\d)[^\d]*$/);
  const body = m ? m[1] : raw;
  const n = parseInt(body.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/** 1245 -> "1 245 kr", the format the store's own money filter produces. */
function nhMoneyText(n) {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + '\u00a0kr';
}

/* ROUND 18 — the SHARED bundle machinery (assets/nordenheim-bundle-core.js).
   Read off the global rather than imported: the section emits that file as a
   deferred classic script through `asset_url`, so it carries the `?v=` a
   relative ESM specifier would have dropped. It is emitted ONLY when
   `bundle_inplace` is on, so on a section without bundle mode this is an empty
   object and nothing below it ever runs. */
const NH_CORE = (typeof window !== 'undefined' && window.NordenheimBundleCore) || {};
const bcFill = NH_CORE.fill || ((s) => String(s || ''));

const TAG = 'nordenheim-plate-snap';

/**
 * Derive every geometry value from one plate rectangle.
 *
 * A cap colourway supplies only where the plate sits on its mockup
 * ({top,left,width,height} as % of the square image). From that we derive:
 *
 *   clip  — the region of the incoming mockup to reveal. Padded outward,
 *           because the pixels just outside the plate are identical between
 *           mockups of the same colourway, so over-clipping is invisible while
 *           under-clipping shaves the plate edge mid-animation.
 *   thumb — background-size/position that frames the plate inside a thumbnail,
 *           reusing the full mockup instead of a separate cropped asset.
 *
 * The thumbnail maths: background-position percentage p aligns the p-point of
 * the image with the p-point of the container, i.e. offset = (box - img) * p.
 * Solving "plate centre lands on container centre" gives the closed forms below.
 */
function derive(rect, thumbAspect, padPercent, vRatio, focus, picFrac) {
  const top = Number(rect.top) || 0;
  const left = Number(rect.left) || 0;
  const width = Number(rect.width) || 0;
  const height = Number(rect.height) || 0;
  const pad = Number(padPercent) || 0;

  // THE STAGE IS A LANDSCAPE WINDOW ONTO A SQUARE COMPOSITE (round 7, Sina),
  // AND SINCE ROUND 10 THE SQUARE MAY BE NARROWER THAN THE WINDOW.
  //
  // Two numbers describe where the picture is, both measured from the live box
  // rather than assumed (see stageGeom()):
  //
  //   vRatio  = the displayed square's SIDE / the box's height. 1 / vRatio is
  //             the fraction of the composite the box shows vertically.
  //   picFrac = the displayed square's SIDE / the box's width, so 1 while the
  //             picture fills the box and less than 1 once a short viewport has
  //             capped the box's height and the picture has narrowed instead.
  //
  // `plate_rect` is a percentage of the SQUARE, so both halves have to be
  // re-expressed as percentages of the BOX or the clip lands off the plate:
  //
  //     top_box    = top_img * vRatio - (vRatio - 1) * f
  //     height_box = height_img * vRatio
  //     left_box   = (1 - picFrac) / 2 + left_img * picFrac
  //     width_box  = width_img * picFrac
  //
  // f is where the vertical crop is anchored — `background-position`'s vertical
  // half as a fraction, read from --nh-bb-stage-focus so the stylesheet and this
  // arithmetic cannot disagree. It is NOT 0.5: the band is anchored to hold the
  // whole cap (13.4%..90% of every composite), not to centre the square.
  //
  // The pad scales with vRatio on the vertical axis and picFrac on the
  // horizontal one, so it stays the same number of PIXELS on both.
  //
  // vRatio = 1 and picFrac = 1 collapse every term back to the original
  // arithmetic, which is what keeps this a superset of the plate-snap copy
  // rather than a fork of it.
  const r = Number(vRatio) > 0 ? Number(vRatio) : 1;
  const f = Number.isFinite(Number(focus)) ? Number(focus) : 0.5;
  const k = Number(picFrac) > 0 ? Math.min(1, Number(picFrac)) : 1;
  const topBox = top * r - (r - 1) * f * 100;
  const heightBox = height * r;
  const padY = pad * r;
  const leftBox = (1 - k) * 50 + left * k;
  const widthBox = width * k;
  const padX = pad * k;

  const clip = {
    top: Math.max(0, topBox - padY),
    right: Math.max(0, 100 - (leftBox + widthBox) - padX),
    bottom: Math.max(0, 100 - (topBox + heightBox) - padY),
    left: Math.max(0, leftBox - padX),
  };

  const pw = width / 100;
  const cx = (left + width / 2) / 100;
  const cy = (top + height / 2) / 100;
  const a = Number(thumbAspect) || 10 / 7;

  let size = 100;
  let posX = 50;
  let posY = 50;
  const denomX = pw - 1;
  const denomY = 1 / a - 1 / pw;
  if (pw > 0 && Math.abs(denomX) > 1e-6 && Math.abs(denomY) > 1e-6) {
    size = 100 / pw;
    posX = ((0.5 * pw - cx) / denomX) * 100;
    posY = ((1 / (2 * a) - cy / pw) / denomY) * 100;
  }

  return {
    clip: `inset(${clip.top.toFixed(2)}% ${clip.right.toFixed(2)}% ${clip.bottom.toFixed(
      2
    )}% ${clip.left.toFixed(2)}% round 5%)`,
    thumb: {
      size: clamp(size, 100, 2000),
      x: clamp(posX, -200, 300),
      y: clamp(posY, -200, 300),
    },
  };
}

function clamp(n, lo, hi) {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

class NordenheimPlateSnap extends HTMLElement {
  connectedCallback() {
    // connectedCallback can fire more than once (e.g. the element is moved).
    // Only build once per connection, and always tear down before rebuilding.
    if (this.ready || this.initIo) return;

    // The build is deferred until the section approaches the viewport. It is
    // real startup work — readData() parses a ~400KB JSON blob, and the first
    // applyCap/selectPlate fetch the stage image, the colourway faces and a
    // preloaded neighbour — and on every page that carries this section it
    // sits below the fold, so at load time all of it only competed with the
    // page's own hero for bandwidth. The margin is generous enough that the
    // stage is painted about a screen before the section scrolls into view.
    if ('IntersectionObserver' in window) {
      this.initIo = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.initIo.disconnect();
          this.initIo = null;
          this.build();
        },
        { rootMargin: '1000px 0px' }
      );
      this.initIo.observe(this);
    } else {
      this.build();
    }
  }

  build() {
    if (this.ready) return;

    const data = this.readData();
    if (!data) return;

    this.data = data;
    // ROUND 18 — THE QUERY STRING AS IT ARRIVED, kept before anything rewrites
    // it. restoreFromUrl() below clicks the real radios, each of which runs
    // syncUrl(), and syncUrl deletes `bundle`/`plates` while bundle mode is off
    // — which is correct, and which had already erased the deep link by the
    // time buildBundle() came to read it. Measured: /?bundle=trio-bundle&
    // plates=a,b landed in plain mode with the address bar rewritten.
    try {
      this.initialParams = new URL(window.location.href).searchParams;
    } catch (e) {
      this.initialParams = null;
    }
    this.settings = data.settings || {};
    // ROUND 18: the core reads every string it shows off `this.labels`, the
    // same name the bundle PDP uses. Plain mode's own strings live here too,
    // so there is one bag rather than two.
    this.labels = this.settings.labels || {};
    this.timers = new Set();
    this.disposers = [];
    // ROUND 14's rule, borrowed: a cold load must not offer the seeded plate
    // as a candidate on a phone — the candidate tile sits in the bar at the
    // same size and place as a chosen one. The first real tap clears it.
    this.untouched = true;

    this.els = {
      stage: this.querySelector('[data-nh-stage]'),
      base: this.querySelector('[data-nh-base]'),
      // ROUND 21 — the viewing zone, which is the shared panel's `.nh-bb__view`
      // and exists only in card mode. The core's paintGround() puts
      // `is-grounded` on it; null off the card, and paintGround returns.
      view: this.querySelector('.nh-bb__view'),
      caption: this.querySelector('[data-nh-caption]'),
      // ROUND 19 — RENAMED. `els.capName` is now the SHARED cap control's
      // button text ("Your cap – Björk Suede Classic"), captured in
      // collectBundleNodes and written by the core's paintCapButton(). This
      // section's own [data-nh-cap-name] has never existed in the markup — the
      // colourway row deliberately prints no name (see the fieldset's comment)
      // — so the rename moves a null, not a behaviour.
      psCapName: this.querySelector('[data-nh-cap-name]'),
      platePrice: this.querySelector('[data-nh-plate-price]'),
      plateName: this.querySelector('[data-nh-plate-name]'),
      plateCollab: this.querySelector('[data-nh-plate-collab]'),
      capNote: this.querySelector('[data-nh-cap-note]'),
      pause: this.querySelector('[data-nh-pause]'),
      pauseLabel: this.querySelector('[data-nh-pause-label]'),
      tablist: this.querySelector('[data-nh-tablist]'),
      atcWrapper: this.querySelector('[data-nh-atc-wrapper]'),
      atcButton: this.querySelector('[data-nh-atc-button]'),
      atcVariant: this.querySelector('[data-nh-atc-variant]'),
      atcPreorder: this.querySelector('[data-nh-atc-preorder]'),
      atcLabel: this.querySelector('[data-nh-atc-label]'),
      atcPrice: this.querySelector('[data-nh-atc-price]'),
      atcName: this.querySelector('[data-nh-atc-name]'),
      atcStatus: this.querySelector('[data-nh-atc-status]'),
      atcError: this.querySelector('[data-nh-atc-error]'),
      capAtcWrapper: this.querySelector('[data-nh-capatc-wrapper]'),
      capAtcButton: this.querySelector('[data-nh-capatc-button]'),
      capAtcVariant: this.querySelector('[data-nh-capatc-variant]'),
      capAtcPlate: this.querySelector('[data-nh-capatc-plate]'),
      capAtcPlateQty: this.querySelector('[data-nh-capatc-plate-qty]'),
      capAtcLabel: this.querySelector('[data-nh-capatc-label]'),
      capAtcPrice: this.querySelector('[data-nh-capatc-price]'),
      capAtcName: this.querySelector('[data-nh-capatc-name]'),
      psSheet: this.querySelector('[data-nh-sheet]'),
      psGrip: this.querySelector('[data-nh-sheet-grip]'),
      psBody: this.querySelector('[data-nh-sheet-body]'),
      peekImg: this.querySelector('[data-nh-peek-img]'),
      peekName: this.querySelector('[data-nh-peek-name]'),
      peekSub: this.querySelector('[data-nh-peek-sub]'),
      rail: this.querySelector('[data-nh-rail]'),
      railCap: this.querySelector('[data-nh-rail-cap]'),
      capAtcStatus: this.querySelector('[data-nh-capatc-status]'),
      capAtcError: this.querySelector('[data-nh-capatc-error]'),
      description: this.querySelector('[data-nh-description]'),
    };
    if (!this.els.stage || !this.els.base) return;

    this.tabs = Array.from(this.querySelectorAll('[data-nh-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-nh-panel]'));
    // Stacked mode renders every category at once and emits no tablist, so the
    // absence of tabs IS the signal. Nothing else in here needs to know how the
    // markup decided that.
    this.stacked = this.tabs.length === 0 && this.panels.length > 0;

    this.capKey = this.pickInitialCap();
    this.catIndex = 0;
    this.plateIndex = 0;
    this.pending = null;
    this.visible = false;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // A reduced-motion preference must override autoplay outright, not merely
    // shorten the animation.
    this.playing = this.settings.autoCycle !== false && !this.reducedMotion.matches;

    this.bind();
    this.applyCap(this.capKey, { animate: false });
    this.selectPlate(0, 0, { animate: false, userInitiated: false });
    this.syncPlayingUi();
    this.startObserving();

    this.ready = true;

    // Shareable combination. Claimed and restored AFTER the default state exists,
    // so restoreFromUrl's clicks land on a section that is already consistent.
    // Gated on a setting so a section that is not the page's main picker (a cap
    // PDP, say) can opt out and leave the address bar alone.
    if (this.settings.syncUrl !== false && this.claimUrl()) {
      this.restoreFromUrl();
    }

    // ROUND 18 — bundle mode. Renders and runs NOTHING unless the section's
    // `bundle_inplace` setting put the markup on the page, and it goes last so
    // the cap and the previewed plate are already whatever the URL asked for
    // before ?bundle= is read.
    this.buildBundle();
  }

  disconnectedCallback() {
    // The init gate may still be waiting; disarm it so a reconnection can
    // re-arm cleanly instead of hitting the connectedCallback guard.
    if (this.initIo) this.initIo.disconnect();
    this.initIo = null;
    if (!this.ready) return;
    this.timers.forEach((t) => clearTimeout(t));
    this.timers.clear();
    if (this.cycleTimer) clearInterval(this.cycleTimer);
    this.cycleTimer = null;
    if (this.io) this.io.disconnect();
    this.io = null;
    this.disposers.forEach((fn) => fn());
    this.disposers = [];
    this.ready = false;
  }

  /* ---------------------------------------------------------------- data */

  readData() {
    const node = this.querySelector('script[type="application/json"][data-nh-data]');
    if (!node) return null;
    try {
      const parsed = JSON.parse(node.textContent);
      if (!parsed || !Array.isArray(parsed.caps) || !Array.isArray(parsed.categories)) return null;
      if (!parsed.caps.length || !parsed.categories.length) return null;
      return parsed;
    } catch (err) {
      // A malformed payload must not take the page down with it.
      console.warn('[nordenheim-plate-snap] could not parse section data', err);
      return null;
    }
  }

  /**
   * Shareable state: ?plate=<handle>&cap=<capkey>
   *
   * Only ONE section per page may own the URL. Mix & Match and a cap PDP can both
   * be on screen, and two instances writing the same two params would each
   * overwrite the other on every click. The first to initialise claims it; the
   * rest behave exactly as before.
   */
  claimUrl() {
    if (NordenheimPlateSnap.urlOwner) return false;
    NordenheimPlateSnap.urlOwner = this;
    return true;
  }

  /**
   * Apply ?plate= / ?cap= if present.
   *
   * Deliberately CLICKS the existing radios rather than calling applyCap and
   * selectPlate directly. Those are only half the job — the category pill, the
   * roving tabindex and the checked radio are all updated by the change handler,
   * and reproducing that here would be a second copy of the same logic waiting
   * to drift. Clicking runs the real path, so a restored link lands in exactly
   * the state a click would have produced.
   *
   * The cap goes first: `platesFor` filters by colourway, so resolving the plate
   * against the wrong cap can miss it or select the wrong index.
   */
  restoreFromUrl() {
    let params;
    try { params = new URL(window.location.href).searchParams; } catch (e) { return; }

    const capWanted = (params.get('cap') || '').trim().toLowerCase();
    if (capWanted) {
      const r = this.qs(`[data-nh-cap-input][value="${cssAttr(capWanted)}"]`);
      if (r) r.click();
    }

    const handle = (params.get('plate') || '').trim();
    if (!handle) return;
    const cats = (this.data && this.data.categories) || [];
    for (let ci = 0; ci < cats.length; ci += 1) {
      const plates = this.platesFor(ci, this.capKey) || [];
      const pi = plates.findIndex((pl) => pl && pl.handle === handle);
      if (pi >= 0) {
        const r = this.querySelector(
          `[data-nh-plate-input][data-cat="${ci}"][data-index="${pi}"]`
        );
        if (r) r.click();
        return;
      }
    }
  }

  /** Reflect the live combination into the address bar, without a history entry. */
  syncUrl() {
    if (NordenheimPlateSnap.urlOwner !== this) return;
    const plate = this.currentPlate && this.currentPlate();
    // ROUND 18.1 — syncUrl is now reached from setBundle() as well, i.e. from
    // a picker row, so a shopper who merely chose "Trio" and nothing else had
    // the SEEDED plate and colourway written into the address bar. A link
    // copied at that moment advertised a selection that was never made. Only a
    // deliberate plate or colourway choice sets `urlTouched`; until it does,
    // the two plain parameters are left exactly as they arrived.
    const writeSelection = !!(plate && plate.handle && this.urlTouched);
    if (!writeSelection && !this.bundleUrlOwned) return;
    try {
      const url = new URL(window.location.href);
      if (writeSelection) {
        url.searchParams.set('plate', plate.handle);
        if (this.capKey) url.searchParams.set('cap', this.capKey);
      }
      // ROUND 18 — the bundle and the tray, written ONLY while bundle mode is
      // on, so a plain-mode query string is byte-identical to what it always
      // was. `plates=` is the same parameter name the bundle PDP restores
      // from, so one link works on both surfaces.
      if (this.bundleOn && this.bundle) {
        url.searchParams.set('bundle', this.bundle.handle);
        const handles = (this.chosen || [])
          .map((c) => (this.plateAt(c.ci, c.pi) || {}).handle)
          .filter(Boolean);
        if (handles.length) url.searchParams.set('plates', handles.join(','));
        else url.searchParams.delete('plates');
      } else if (this.bundleUrlOwned) {
        // ONLY AN OWNER DELETES. This section never wrote `bundle`/`plates`
        // until bundle mode was entered on this page load, and a campaign or
        // analytics link that happened to use either name had them dropped on
        // the shopper's first plate click. `bundleUrlOwned` is set the moment
        // this section takes the two parameters over — a picker row, an
        // in-page bundle link, or a deep link it restored from.
        url.searchParams.delete('bundle');
        url.searchParams.delete('plates');
      }
      // replaceState, not pushState: flicking through colourways is not
      // navigation, and pushing would make Back walk every swatch that was tried.
      window.history.replaceState(window.history.state, '', url);
    } catch (e) { /* a hostile URL is not worth breaking the section over */ }
  }

  pickInitialCap() {
    const wanted = this.getAttribute('data-default-cap');
    const found = this.data.caps.find((c) => c.key === wanted);
    return (found || this.data.caps[0]).key;
  }

  /**
   * ROUND 18 — LOOK UP ON THE SECTION, NOT ON THIS ELEMENT.
   *
   * In bundle mode on a phone `.nh-ps__media` — the stage, the caption, the
   * colourway swatches, the editorial rail — moves into the dock, which is a
   * SIBLING of this custom element. `this.querySelector` stops finding any of
   * it, silently: the swatch row still paints, and choosing a colourway simply
   * does nothing. The section contains both boxes in every mode, so scoping
   * there is a safe superset and never reaches another instance.
   *
   * Only the lookups that can travel use these. Everything captured in
   * `this.els` at build time is held by reference and needs neither.
   */
  qs(sel) {
    return (this.bcRoot() || this).querySelector(sel);
  }

  qsa(sel) {
    return Array.from((this.bcRoot() || this).querySelectorAll(sel));
  }

  /**
   * ROUND 19 — THIS SECTION'S OWN "Shown on <cap>" line.
   *
   * In bundle mode `els.capNote` is re-pointed at the SHARED caption row (the
   * "<Plate> × <Cap>" band the bundle page has), because that is what the
   * core's paintCapNote() writes and round 19's rule is one caption in one
   * format on both surfaces. This section's own line is kept — it is inside
   * `.nh-ps__meta`, which the card layout hides — and every writer in this file
   * goes through here so the DEFAULT path, where `psCapNote` is never set, is
   * byte-for-byte what it was.
   */
  psNote() {
    return this.els.psCapNote || this.els.capNote;
  }

  cap(key) {
    return this.data.caps.find((c) => c.key === (key || this.capKey)) || this.data.caps[0];
  }

  /**
   * Plates in a category that actually have a mockup for the given colourway.
   * The image matrix is filled in over time, so a plate with no mockup for the
   * selected cap is skipped rather than rendered as a broken tile.
   */
  platesFor(catIndex, capKey) {
    const cat = this.data.categories[catIndex];
    if (!cat) return [];
    return cat.plates.filter((p) => stageSrc(p, capKey));
  }

  /* -------------------------------------------------------------- events */

  on(target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    this.disposers.push(() => target.removeEventListener(type, handler, opts));
  }

  bind() {
    if (this.els.pause) {
      this.on(this.els.pause, 'click', () => {
        this.setPlaying(!this.playing, { explicit: true });
      });
    }

    // Category tabs — automatic activation, roving tabindex.
    this.tabs.forEach((tab, i) => {
      this.on(tab, 'click', () => this.selectCategory(i, { focusTab: true }));
      this.on(tab, 'keydown', (e) => this.onTabKeydown(e, i));
    });

    // Plate + cap radios. Change is delegated so tabs re-rendered by the editor
    // do not need rebinding.
    this.onPsChange = (e) => {
      const plate = e.target.closest('[data-nh-plate-input]');
      if (plate) {
        const ci = Number(plate.dataset.cat);
        const pi = Number(plate.dataset.index);
        this.selectPlate(ci, pi, { animate: true, userInitiated: true });
        this.haltAutoplay();
        return;
      }
      const cap = e.target.closest('[data-nh-cap-input]');
      if (cap) {
        // An explicit colourway choice sticks. Someone who picked Midnatt is
        // browsing plates ON Midnatt, so later plates must not yank them back to
        // the lead worn shot (which is a different cap entirely).
        this.userPick = { type: 'cap' };
        this.applyCap(cap.value, { animate: true });
        this.urlTouched = true;
        this.syncUrl();
        this.haltAutoplay();
      }
    };
    // NAMED, because in bundle mode on a phone the colourway swatches move
    // into the dock — a sibling of this element — and a delegated listener on
    // `this` never sees them. buildBundle() binds the same function on the
    // dock as well (round 15's lesson, one file over): measured before the
    // fix, a swatch tap inside the sheet left `capKey` and the posted bundle
    // variant on the previous colourway.
    this.on(this, 'change', this.onPsChange);

    // Preload on intent: hovering or focusing a thumb is a strong signal that
    // it is about to be shown, and it is far cheaper than preloading a whole
    // category across every colourway.
    const intent = (e) => {
      const label = e.target.closest('[data-nh-plate-src]');
      if (label) this.preload(label.dataset.nhPlateSrc);
    };
    this.on(this, 'pointerover', intent);
    this.on(this, 'focusin', intent, true);

    // Pausing on hover/focus is a courtesy on top of the explicit control,
    // never a replacement for it.
    this.on(this, 'pointerenter', () => { this.hovering = true; });
    this.on(this, 'pointerleave', () => { this.hovering = false; });
    this.on(this, 'focusin', () => {
      this.focused = true;
      // A keyboard user inside the widget should hear caption changes.
      if (this.els.caption) this.els.caption.setAttribute('aria-live', 'polite');
    });
    this.on(this, 'focusout', () => {
      if (!this.contains(document.activeElement)) this.focused = false;
    });

    if (this.els.atcButton) {
      this.on(this.els.atcButton, 'click', (e) => this.psOnAddToCart(e));
      // The theme dispatches both of these ON the button, and they bubble.
      this.on(this.els.atcButton, 'theme:product:add', () => this.psAnnounceAdd(true));
      this.on(this.els.atcButton, 'theme:product:add-error', (e) => this.psAnnounceAdd(false, e));
    }

    // Colourway stepping from the picture: arrows, and a horizontal drag.
    this.querySelectorAll('[data-nh-cap-nav]').forEach((b) => {
      this.on(b, 'click', (e) => {
        e.preventDefault();
        this.stepCap(Number(b.getAttribute('data-nh-cap-nav')) || 1);
      });
    });
    if (this.els.stage) this.psBindStageSwipe(this.els.stage);
    if (this.els.psSheet) this.initSheet();
    if (this.els.railCap) this.on(this.els.railCap, 'click', () => this.exitEditorial());

    // The cap button is a second, independent purchase. Same three listeners,
    // bound to ITS button so the theme's events never cross-write the other
    // form's status region.
    if (this.els.capAtcButton) {
      this.on(this.els.capAtcButton, 'click', (e) => this.psOnAddToCart(e, true));
      this.on(this.els.capAtcButton, 'theme:product:add', () => this.psAnnounceAdd(true, null, true));
      this.on(this.els.capAtcButton, 'theme:product:add-error', (e) => this.psAnnounceAdd(false, e, true));
    }

    this.on(document, 'visibilitychange', () => {
      if (document.hidden) this.stopTimer();
      else if (this.playing) this.startTimer();
    });

    const onMotionChange = (e) => {
      if (e.matches) this.setPlaying(false, { explicit: false });
    };
    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener('change', onMotionChange);
      this.disposers.push(() => this.reducedMotion.removeEventListener('change', onMotionChange));
    }
  }

  onTabKeydown(e, i) {
    // Navigate across the tabs that are actually shown. A category can be
    // hidden when it has no mockups in the current colourway, and arrowing onto
    // a display:none tab would silently drop focus.
    const live = this.tabs.map((t, idx) => (t.hidden ? -1 : idx)).filter((idx) => idx >= 0);
    if (live.length < 2) return;

    const at = live.indexOf(i);
    const last = live.length - 1;
    let pos = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') pos = at === last ? 0 : at + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') pos = at === 0 ? last : at - 1;
    else if (e.key === 'Home') pos = 0;
    else if (e.key === 'End') pos = last;
    if (pos === null) return;

    e.preventDefault();
    this.selectCategory(live[pos], { focusTab: true });
  }

  /* ------------------------------------------------------------ playback */

  startObserving() {
    // The section sits partway down the page, so nothing should animate until
    // it is actually on screen.
    if (!('IntersectionObserver' in window)) {
      this.visible = true;
      if (this.playing) this.startTimer();
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          if (this.visible && this.playing) this.startTimer();
          else this.stopTimer();
        });
      },
      { threshold: 0.25 }
    );
    this.io.observe(this);
  }

  startTimer() {
    this.stopTimer();
    const seconds = Number(this.settings.cycleSeconds) || 3.2;
    this.cycleTimer = setInterval(() => this.advance(), Math.max(1.5, seconds) * 1000);
  }

  stopTimer() {
    if (this.cycleTimer) clearInterval(this.cycleTimer);
    this.cycleTimer = null;
  }

  setPlaying(on, { explicit }) {
    this.playing = on;
    if (on && this.visible) this.startTimer();
    else this.stopTimer();
    this.syncPlayingUi();
    if (explicit && on) this.userStopped = false;
  }

  /**
   * Any manual selection latches autoplay off for good. WCAG 2.2.2 requires
   * that once stopped it does not resume without an explicit request, and a
   * timed auto-resume would also fight the person browsing the grid.
   */
  haltAutoplay() {
    if (!this.playing) return;
    this.userStopped = true;
    this.setPlaying(false, { explicit: false });
  }

  syncPlayingUi() {
    const { pause, pauseLabel, caption } = this.els;
    if (pause) {
      // The label is visible text, so the accessible name comes from content —
      // more reliable than swapping aria-label, and it satisfies Label in Name.
      if (pauseLabel) pauseLabel.textContent = this.playing ? 'Pause' : 'Play';
      pause.setAttribute(
        'aria-label',
        this.playing ? 'Pause the plate slideshow' : 'Play the plate slideshow'
      );
      pause.dataset.state = this.playing ? 'playing' : 'paused';
    }
    if (caption) {
      // While it rotates on its own the live region is silenced, or it would
      // interrupt whatever the person is actually reading.
      caption.setAttribute('aria-live', this.playing && !this.focused ? 'off' : 'polite');
    }
  }

  advance() {
    if (this.hovering || this.focused || document.hidden || this.pending) return;
    const plates = this.platesFor(this.catIndex, this.capKey);

    // On a plate product page there is only ever one plate, so cycling plates
    // would do nothing. Cycle the COLOURWAYS instead — that is the thing worth
    // showing there: this design on each cap.
    if (plates.length < 2) {
      if (this.data.caps.length < 2) return;
      const i = this.data.caps.findIndex((c) => c.key === this.capKey);
      const next = this.data.caps[(i + 1) % this.data.caps.length];
      this.applyCap(next.key, { animate: true });
      return;
    }

    this.selectPlate(this.catIndex, (this.plateIndex + 1) % plates.length, {
      animate: true,
      userInitiated: false,
    });
  }

  /* ------------------------------------------------------------ selection */

  selectCategory(index, { focusTab } = {}) {
    if (index === this.catIndex) return;
    this.catIndex = index;

    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      // Roving tabindex: only the active tab is in the tab sequence.
      if (on) tab.removeAttribute('tabindex');
      else tab.setAttribute('tabindex', '-1');
      if (on && focusTab) tab.focus();
    });
    // Stacked keeps every panel on screen — hiding them here would be the tabbed
    // behaviour leaking into a layout that has no tabs to explain it.
    if (!this.stacked) {
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }

    this.haltAutoplay();
    this.selectPlate(index, 0, { animate: true, userInitiated: true });
  }

  selectPlate(catIndex, plateIndex, { animate, userInitiated }) {
    const plates = this.platesFor(catIndex, this.capKey);
    if (!plates.length) return;

    const idx = Math.max(0, Math.min(plateIndex, plates.length - 1));
    const plate = plates[idx];
    const src = stageSrc(plate, this.capKey);
    if (!src) return;

    this.catIndex = catIndex;
    this.plateIndex = idx;
    // ROUND 20 — RULE 3: A PLATE CLICK RETURNS THE PANEL TO TODAY'S BEHAVIOUR,
    // exactly. Left before syncRail() below, which is what re-renders the
    // shared gallery band: with the flag still set the band would draw the cap's
    // photographs under a plate on the stage.
    // `repaint: false` because everything applyCap would redo, this function
    // does for itself a few lines down.
    this.exitSoloCap({ repaint: false });

    this.syncRadio(catIndex, plate.id);
    // Only a deliberate choice is written to the URL. Autoplay also lands here
    // (userInitiated: false), and reflecting every cycle tick would rewrite the
    // address bar a few times a minute and make any copied link a lottery.
    if (userInitiated) {
      this.untouched = false;
      // ROUND 18.1 — the flag syncUrl() reads. Separate from `untouched`,
      // which also decides whether the seeded plate is a bundle candidate: a
      // colourway choice must write the URL (it always has) without turning
      // the plate nobody has looked at into a candidate.
      this.urlTouched = true;
      this.syncUrl();
    }
    this.paintCaption(plate, userInitiated);
    this.syncAddToCart(plate);
    this.syncDescription(plate);
    // The cap button carries this plate as its second line item, so it is stale
    // the moment the plate changes — not only when the colourway does.
    this.syncCapAddToCart(this.psBoughtCap());
    this.syncPeek(plate);
    // Choosing a plate is a request to SEE it, so the sheet reveals itself and
    // opens — but only on a real tap. Autoplay also calls selectPlate, and a
    // sheet that appeared and flew open by itself every few seconds would be
    // unusable. Before the first tap there is no chosen combination to show.
    // ROUND 18 — THE TRAY FOLLOWS THE STAGE. A plate tap is what makes that
    // plate the candidate, so the tray, the count and the sheet's own button
    // are redrawn here — the same three things the bundle PDP's preview() does.
    // Before the tray, because setSheet() below moves focus onto the button
    // whose label this decides.
    // ROUND 19.1 — `cardOn()`, not `bundleOn`. In Starter and "Plates only" the
    // tray's single slot MIRRORS the stage, so it has to be redrawn by exactly
    // the same event that moved the stage. `announceCandidate()` stays behind
    // the bundle test: there is no candidate to announce in a mode whose one
    // slot is not a choice, and the count region would have read "1 of 1
    // chosen" for a tile nobody chose.
    if (this.cardOn()) this.renderTray();
    if (this.bundleOn) this.announceCandidate();
    if (userInitiated) {
      // Exactly ONE shell is in the layout at a time (psPlace decides which),
      // so exactly one of these two branches can be right. In bundle mode on a
      // phone it is the shared dock; everywhere else it is this section's own
      // bottom sheet, unchanged.
      if (this.sheetActive()) {
        this.setSheet(true);
      } else {
        this.revealSheet();
        this.psSetSheet(true);
      }
    }
    // A new plate invalidates the open photo — its shots belong to the old one.
    this.exitEditorial({ repaint: false });
    this.syncRail(plate);

    if (!animate) {
      this.setBase(src);
      this.openLeadShot();
      this.preloadNeighbour();
      return;
    }
    this.snapTo(src);
    this.openLeadShot();
    this.preloadNeighbour();
  }

  syncRadio(catIndex, plateId) {
    const input = this.querySelector(
      `[data-nh-plate-input][data-cat="${catIndex}"][value="${cssAttr(plateId)}"]`
    );
    if (input && !input.checked) input.checked = true;
    // ROUND 18 — THE ONE PLACE THE BUNDLE'S PLATE REFERENCE COMES FROM.
    // `plateIndex` is an index into platesFor()'s colourway-filtered list
    // while every tile's data-index is an index into the raw payload array;
    // the two diverge for a plate with a mockup on some colourways and not
    // others. Reading it back off the radio that was actually CHECKED means
    // the tray, the ticks and the posted variant name the same product the
    // stage is showing, whatever selectPlate was passed — without touching
    // the default path, where the discrepancy is pre-existing and harmless.
    if (input) {
      this._previewed = { ci: catIndex, pi: Number(input.dataset.index) };
    }
  }

  paintCaption(plate, userInitiated) {
    if (this.bundleOn) this.syncSheetTitle();
    const el = this.els.caption;
    if (!el) return;

    const write = () => {
      const link = el.querySelector('a');
      if (link) {
        link.textContent = plate.name;
        if (plate.url) link.setAttribute('href', plate.url);
      } else {
        el.textContent = plate.name;
      }
      // The plate's own price, kept off the cap line where it read as the
      // cap's price. Falls back to empty rather than showing a stale number.
      if (this.els.platePrice) this.els.platePrice.textContent = plate.price || '';
      if (this.els.plateName) {
        // Same span treatment as the cap link, so both underline identically and
        // neither underlines its arrow.
        let t = this.els.plateName.querySelector('span');
        if (!t) {
          t = document.createElement('span');
          this.els.plateName.textContent = '';
          this.els.plateName.appendChild(t);
        }
        // Just the product name. "shown on <cap>" and "— plate only" already say
        // what the thing is, so spelling out the product term as well only made
        // the line longer.
        t.textContent = plate.name || '';
        if (plate.url) this.els.plateName.setAttribute('href', plate.url);
      }
      // Partnership chip. `hidden` is toggled rather than the text merely
      // cleared, so an empty chip never occupies a row of its own for the 195
      // plates that are not collaborations.
      if (this.els.plateCollab) {
        const collab = plate.collab || '';
        // The <p> owns the row (flex-basis:100%); the pill has to be an inner
        // span or the background stretches the full width of the meta block.
        let label = this.els.plateCollab.querySelector('[data-nh-collab-label]');
        let amount = this.els.plateCollab.querySelector('[data-nh-collab-amount]');
        if (!label) {
          this.els.plateCollab.textContent = '';
          label = document.createElement('span');
          label.setAttribute('data-nh-collab-label', '');
          amount = document.createElement('span');
          amount.setAttribute('data-nh-collab-amount', '');
          this.els.plateCollab.append(label, amount);
        }
        label.textContent = collab;
        // The donation fact is optional: a partnership that does not donate
        // still gets the strip, just without a right-hand half.
        const amt = plate.collabAmount || '';
        amount.textContent = amt;
        amount.hidden = !amt;
        this.els.plateCollab.hidden = !collab;
      }
    };

    if (!userInitiated) {
      write();
      return;
    }
    // A user-initiated change is already announced by the radio itself
    // ("Aurora, radio button, selected, 3 of 12"), so mute the live region
    // around the mutation to avoid double-speak, then restore it.
    const prev = el.getAttribute('aria-live');
    el.setAttribute('aria-live', 'off');
    write();
    const t = setTimeout(() => {
      el.setAttribute('aria-live', prev || 'polite');
      this.timers.delete(t);
    }, 250);
    this.timers.add(t);
  }

  /* ------------------------------------------------------------ animation */

  /**
   * Start a swap once the incoming image is actually ready to paint.
   *
   * A CSS animation on an element whose background is still downloading does
   * not visibly begin until the bytes arrive — measured at ~1.15s on a cold
   * image, which reads as "the click did nothing". Decoding first means the
   * snap always plays on a loaded image. Cached images decode synchronously,
   * so the common case stays instant, and a timeout stops a slow network from
   * blocking the swap indefinitely.
   */
  snapTo(src) {
    const token = (this.swapSeq = (this.swapSeq || 0) + 1);
    let started = false;
    const start = () => {
      // A newer swap (or a colourway change) supersedes this one.
      if (started || token !== this.swapSeq || !this.ready) return;
      started = true;
      this.beginSnap(src);
    };

    const img = new Image();
    img.decoding = 'async';
    img.src = src;
    if (img.complete) {
      start();
      return;
    }
    img.decode().then(start, start);
    const t = setTimeout(start, 400);
    this.timers.add(t);
  }

  beginSnap(src) {
    const stage = this.els.stage;

    // Retarget an in-flight swap rather than queueing: rapid clicking should
    // land on the last thing clicked, not replay every step.
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      if (this.pendingSrc) this.setBase(this.pendingSrc);
    }

    const layer = document.createElement('div');
    layer.className = 'nh-ps__layer nh-ps__layer--incoming';
    layer.style.backgroundImage = `url("${cssUrl(src)}")`;
    layer.setAttribute('aria-hidden', 'true');

    this.pending = layer;
    this.pendingSrc = src;

    // Runs at most once. Without the latch the fallback timer below fires a
    // second time after a normal animationend and re-writes `src` — which
    // silently reverts any colourway change made in the meantime.
    let committed = false;
    let guard = null;
    const commit = () => {
      if (committed) return;
      committed = true;
      if (guard !== null) {
        clearTimeout(guard);
        this.timers.delete(guard);
      }
      layer.remove();
      // Only claim the stage if this swap is still the current one; a later
      // swap or a colourway change may already own it.
      if (this.pending === layer) {
        this.setBase(src);
        this.pending = null;
        this.pendingSrc = null;
      }
    };
    layer.addEventListener('animationend', commit, { once: true });
    // animationend never fires if the layer is removed or animations are off,
    // so a timer guarantees the base image still lands.
    guard = setTimeout(commit, 1200);
    this.timers.add(guard);

    stage.appendChild(layer);
  }

  /* ------------------------------------------------------------ mobile sheet */

  /**
   * Below 900px the cap panel is relocated into the sheet; above it, back into
   * its column.
   *
   * MOVED, not cloned. A copy would mean two stages, two radio groups with the
   * same name, and two add-to-cart forms — every handler would need to know
   * which one it was talking to, and the two would drift. Moving the live node
   * keeps checked state, listeners and inline geometry intact, because the DOM
   * carries them along.
   */
  initSheet() {
    const { psSheet: sheet, psBody: sheetBody, psGrip: sheetGrip } = this.els;
    const media = this.querySelector('.nh-ps__media');
    if (!sheet || !sheetBody || !media) return;

    this.classList.add('nh-ps--sheet');
    this.mediaHome = media.parentNode;
    this.mediaNext = media.nextSibling;

    // ROUND 22, ITEM G — the dock's Add button travels into the panel with the
    // picture. Its home is recorded the same way and for the same reason as the
    // panel's: it has to go back byte-for-byte on a resize to desktop, and
    // `appendChild` would drop it after the strip instead of before it.
    this.extrasMark = this.querySelector('[data-nh-ps-extras-mark]');
    const sheetAddBtn = this.els.sheetAdd;
    if (sheetAddBtn) {
      this.sheetAddHome = sheetAddBtn.parentNode;
      this.sheetAddNext = sheetAddBtn.nextSibling;
    }

    const mq = window.matchMedia('(max-width: 900px)');
    this.mq = mq;
    /**
     * ROUND 18 — ONE MOVER, TWO POSSIBLE DESTINATIONS.
     *
     * The picture panel goes into this section's own bottom sheet, as it
     * always has — unless bundle mode's DOCK is the phone layout, in which
     * case it goes into the dock's sheet body instead. There is never a
     * moment with two grips, two scrims or two z-2400 boxes: whichever shell
     * is not the destination is `hidden`, and this function is what decides.
     *
     * Idempotent (it compares parentNode), which is what lets the resize net
     * beside matchMedia run it twice for free — and what lets setBundle() just
     * call it again on a mode switch.
     */
    this.psPlace = () => {
      // sheetActive(), which includes the BREAKPOINT. `dockMode()` alone is a
      // section setting and stays true on a 1440 screen — the media was left
      // stranded in the dock on a resize back to desktop, and the page went
      // into horizontal scroll behind it (measured 1475 against 1425).
      const dockHost = this.sheetActive() ? this.els.sheetHost : null;
      this.psPlaceSheetAdd(!!dockHost);
      if (dockHost) {
        if (media.parentNode !== dockHost) dockHost.appendChild(media);
        // This section's own sheet gives up the phone entirely while the dock
        // owns it: hidden, un-revealed, and parked.
        sheet.hidden = true;
        sheet.classList.remove('is-open', 'is-revealed');
        this.classList.remove('has-sheet');
        return;
      }
      if (mq.matches) {
        if (media.parentNode !== sheetBody) sheetBody.appendChild(media);
        sheet.hidden = false;
        if (this.sheetRevealed) sheet.classList.add('is-revealed');
        if (this.sheetRevealed) this.classList.add('has-sheet');
      } else {
        if (media.parentNode !== this.mediaHome) {
          this.mediaHome.insertBefore(media, this.mediaNext);
        }
        sheet.hidden = true;
        this.psSetSheet(false);
      }
    };
    const place = this.psPlace;
    place();

    // Two triggers on purpose. matchMedia('change') is the correct one, but it
    // was observed not firing on a programmatic viewport change, and the failure
    // mode is severe: the panel stays stranded in a display:none sheet, so the
    // cap, the colourways and add-to-cart all vanish on desktop. A debounced
    // resize is the safety net — place() is idempotent, so running it twice
    // costs a parentNode comparison and nothing else.
    if (mq.addEventListener) {
      mq.addEventListener('change', place);
      this.disposers.push(() => mq.removeEventListener('change', place));
    }
    let t = null;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        place();
        this.psParkSheet();
        // ROUND 21 — the bleed's precondition is a RATIO of the measured box, and
        // the box's height comes from a 100vh budget: a window resize can cross
        // the 68% threshold in either direction with no other event to hang it
        // off. Cheap (one getBoundingClientRect) and already debounced by 120ms.
        this.syncStageBleed();
      }, 120);
    };
    this.on(window, 'resize', onResize);
    this.on(window, 'orientationchange', onResize);
    this.disposers.push(() => clearTimeout(t));

    if (sheetGrip) {
      this.on(sheetGrip, 'click', () => this.psSetSheet(!sheet.classList.contains('is-open')));
      this.psBindSheetDrag(sheetGrip, sheet);
    }
    // The collapsed row is a much larger target than the bar alone, and people
    // aim at the picture, not the 6px line above it. Pointer-only on purpose:
    // the grip button already carries the label, the focus ring and the keyboard
    // path, so making this focusable too would just duplicate a tab stop.
    // Drag, not just tap. People aim at the picture and pull upward, and binding
    // the gesture only to the 19px handle made most of those swipes land on dead
    // pixels — which read as the sheet being unresponsive rather than as a miss.
    // The whole 105px row now drags, and a tap with no movement still opens it.
    const peek = this.querySelector('[data-nh-peek]');
    if (peek) this.psBindSheetDrag(peek, sheet, () => this.psSetSheet(true));
    this.syncPeek(this.currentPlate());
    this.psParkSheet();
  }

  /**
   * Recompute where the sheet parks, in pixels.
   *
   * Must run whenever the viewport height changes: the sheet is 100dvh, so its
   * parked offset is a function of a number that moves when mobile browser
   * chrome appears. Cheap enough to call freely.
   */
  psParkSheet() {
    const sheet = this.els.psSheet;
    if (!sheet) return;
    const y = Math.max(0, sheet.offsetHeight - this.psPeekHeight());
    sheet.style.setProperty('--nh-ps-parked', y + 'px');
  }

  /** First real plate choice brings the sheet into existence. Idempotent. */
  revealSheet() {
    const sheet = this.els.psSheet;
    if (!sheet || this.sheetRevealed) return;
    this.sheetRevealed = true;
    this.psParkSheet();
    sheet.classList.add('is-revealed');
    this.classList.add('has-sheet');
  }

  psSetSheet(open) {
    const { psSheet: sheet, psGrip: sheetGrip } = this.els;
    if (!sheet || !this.sheetRevealed) return;
    this.psParkSheet();
    sheet.classList.toggle('is-open', open);
    if (sheetGrip) {
      const labels = this.settings.labels || {};
      sheetGrip.setAttribute('aria-expanded', String(open));
      sheetGrip.setAttribute(
        'aria-label',
        open ? labels.sheetClose || 'Hide the cap' : labels.sheetOpen || 'Show the cap'
      );
    }
  }

  /**
   * Drag the handle. Commits on DISTANCE past a threshold rather than on release
   * velocity: a slow deliberate pull should behave the same as a flick, and
   * velocity punishes the former.
   */
  psBindSheetDrag(grip, sheet, onTap) {
    let y0 = null;
    let wasOpen = false;
    let moved = false;

    // A drag that ends past the threshold commits the new state — and then the
    // browser fires `click` on the same element, which toggled it straight back.
    // That is the intermittent "swipe does nothing": it opened and shut inside
    // one gesture. Swallow the click that follows a real drag; a tap (no
    // movement) still falls through to the handler.
    this.on(grip, 'click', (e) => {
      if (!moved) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      moved = false;
    }, true);

    this.on(grip, 'pointerdown', (e) => {
      y0 = e.clientY;
      moved = false;
      wasOpen = sheet.classList.contains('is-open');
      sheet.classList.add('is-dragging');
      if (grip.setPointerCapture) grip.setPointerCapture(e.pointerId);
    });
    this.on(grip, 'pointermove', (e) => {
      if (y0 === null) return;
      const dy = e.clientY - y0;
      if (Math.abs(dy) > 4) moved = true;
      // The sheet is full height and placed by transform, so the finger can be
      // tracked in BOTH directions — there is no half-open state with clipped
      // content to avoid any more. Clamped to the same range the two resting
      // positions occupy so it can never be dragged off its own track.
      const parked = sheet.offsetHeight - this.psPeekHeight();
      const base = wasOpen ? 0 : parked;
      const next = Math.max(0, Math.min(base + dy, parked));
      // Inline transform during the drag: it outranks both resting rules, so the
      // sheet follows the finger regardless of which state it started in.
      sheet.style.transform = `translateY(${next}px)`;
    });
    const finish = (e) => {
      if (y0 === null) return;
      const dy = e.clientY - y0;
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
      if (Math.abs(dy) > 40) this.psSetSheet(dy < 0);
      else if (!moved && onTap) onTap();
      else this.psSetSheet(wasOpen);
      y0 = null;
    };
    this.on(grip, 'pointerup', finish);
    this.on(grip, 'pointercancel', () => {
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
      y0 = null;
    });
  }

  /** How much of the sheet stays on screen when parked, read from the CSS so the
   *  two never drift apart. */
  psPeekHeight() {
    const v = getComputedStyle(this.els.psSheet).getPropertyValue('--nh-ps-peek-h');
    return parseFloat(v) || 92;
  }

  /** The collapsed row: enough to know what is selected without opening it. */
  syncPeek(plate) {
    const { peekImg, peekName, peekSub } = this.els;
    if (!peekName || !plate) return;
    const cap = this.cap(this.capKey);
    const img = (plate.images || {})[this.capKey];
    if (peekImg && img && img.thumb) {
      peekImg.style.backgroundImage = `url("${cssUrl(img.thumb)}")`;
    }
    peekName.textContent = plate.name || '';
    const bits = [];
    if (plate.price) bits.push(plate.price);
    if (cap && cap.name) bits.push(cap.name);
    peekSub.textContent = bits.join(' \u00b7 ');
  }

  /**
   * Move one view along the strip, wrapping at both ends.
   *
   * The strip is ONE radio group in DOM order — the worn shots, then the
   * artwork, then the eight colourways — so "next" has to mean the next tile.
   * This used to walk `data.caps` instead, which is only the eight mockups: the
   * on-screen arrows silently skipped every photo while the arrow KEYS, which
   * the browser drives off the radio group, traversed all of them. Two controls
   * over the same strip disagreeing about what it contains.
   *
   * Clicking the input rather than calling applyCap/showEditorial directly keeps
   * one code path. The change handlers already know how to open each kind of
   * tile, and they record `userPick` exactly as a real click or arrow key would,
   * so a choice made with the arrows persists across plates like any other.
   *
   * Deliberately still not the PLATE axis: the stage answers "this plate, on
   * this cap", and a control that changed the plate would fight the grid
   * selection and the add-to-cart beneath it.
   *
   * In a cap-only view it means something else entirely — see the first branch
   * of the body. There the strip holds one cap's photographs rather than the
   * colourway axis, so the arrows page through those instead.
   */
  /**
   * The stage's real geometry, measured — the bundle page's stageGeom() for a
   * `cover` layer.
   *
   * The layers are `background-size: cover; background-position: center` on a
   * box that is no longer square, so a SQUARE composite is scaled to the box's
   * width and cropped equally top and bottom. That makes the three numbers
   * derive() wants trivial: the square's side is the box's width, so
   * vRatio = width / height, picFrac = 1, and the crop is centred (focus 0.5).
   *
   * Measured, never assumed, for the same reason the bundle page measures: the
   * box's height is capped by the viewport, so the ratio is a runtime fact.
   * A square box gives vRatio 1 and the arithmetic collapses to what it was.
   */
  stageGeom() {
    const none = { vRatio: 1, picFrac: 1, focus: 0.5 };
    const st = this.els && this.els.stage;
    if (!st) return none;
    const r = st.getBoundingClientRect();
    if (!r.width || !r.height) return none;
    // The bundle page's stageGeom(), verbatim: the picture's side is
    // min(box width, box height * fill) — the same number --nh-bb-pic-w
    // resolves to in the shared stylesheet, read from it rather than guessed.
    // `background-size: cover` on a SQUARE composite: the side is the box's
    // larger dimension, i.e. its width in a landscape box. picFrac is therefore
    // 1 and the whole correction is vertical — which is what vRatio carries.
    const pic = Math.max(r.width, r.height);
    if (!(pic > 0)) return none;
    return { vRatio: Math.max(1, pic / r.height), picFrac: Math.min(1, pic / r.width), focus: 0.5 };
  }

  stepCap(delta) {
    // ROUND 22, ITEM 0 — STEPPING WHILE A CAP IS OPEN WALKS THAT CAP'S PHOTOS.
    //
    // This REVERSES round 20, which made the arrows change the cap on the
    // grounds that they are the colourway axis everywhere else. Sina, 2026-09-04,
    // looking at the Faluröd cap on the bundle PDP: "when clicking the arrow
    // icons on left/right of the cap, we are supposed to swap images of the same
    // cap, but currently they swap the cap itself."
    //
    // He is right about what the control means here. In a cap-only view the
    // arrows sit ON the photograph, directly above a strip of that cap's other
    // photographs, so they read as a gallery pager — and the cap itself has
    // three other ways to change (the cap row, the chips, the dropdown), none of
    // which is what a ‹ › over a picture looks like.
    //
    // openShot() is the same entry point a strip chip uses, so the chip's
    // pressed state, `shotIndex` and the live region all stay in step for free.
    // The stage swipe funnels through here too and therefore follows.
    if (this.soloCapKey) {
      const shots = this.capMedia(this.soloCap());
      if (shots.length < 2) return;
      // paintSoloCap() clamps `shotIndex` into range every time it draws, so it
      // is a real index here and never the -1 that means "the real combination".
      const at = Math.max(0, Math.min(this.shotIndex, shots.length - 1));
      const next = ((at + delta) % shots.length + shots.length) % shots.length;
      if (next !== at) this.openShot(next);
      return;
    }
    const group = (this.qs('[data-nh-cap-input]') || {}).name;
    if (group) {
      const radios = Array.from(
        this.qsa(`input[type="radio"][name="${cssAttr(group)}"]`)
      );
      if (radios.length > 1) {
        let i = radios.findIndex((r) => r.checked);
        if (i < 0) i = 0;
        const next = radios[((i + delta) % radios.length + radios.length) % radios.length];
        if (next && next !== radios[i]) next.click();
        return;
      }
    }

    // No group in the DOM — the cap page can render a single colourway with no
    // strip at all — so fall back to the colourway axis.
    const caps = (this.data && this.data.caps) || [];
    if (caps.length < 2) return;
    const i = caps.findIndex((c) => c.key === this.capKey);
    const next = caps[((i + delta) % caps.length + caps.length) % caps.length];
    if (!next || next.key === this.capKey) return;
    this.applyCap(next.key, { animate: true });
    const input = this.qs(`[data-nh-cap-input][value="${cssAttr(next.key)}"]`);
    if (input && !input.checked) input.checked = true;
  }

  /**
   * Horizontal drag on the stage steps the colourway.
   *
   * Pointer Events rather than touch*, so a trackpad drag and a finger behave
   * the same. The gesture only claims the interaction once it is clearly
   * horizontal (dx past a threshold AND meaningfully larger than dy) — on a
   * phone the stage fills the screen, so treating an ambiguous drag as a swipe
   * would make the page impossible to scroll past.
   */
  psBindStageSwipe(stage) {
    let x0 = null, y0 = null, id = null;
    const THRESHOLD = 45;

    this.on(stage, 'pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      x0 = e.clientX; y0 = e.clientY; id = e.pointerId;
    });
    const end = (e) => {
      if (x0 == null || e.pointerId !== id) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = y0 = id = null;
      if (Math.abs(dx) < THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.stepCap(dx < 0 ? 1 : -1);
    };
    this.on(stage, 'pointerup', end);
    this.on(stage, 'pointercancel', () => { x0 = y0 = id = null; });
  }

  applyCap(key, { animate }) {
    const cap = this.cap(key);
    if (!cap) return;
    this.capKey = cap.key;
    // ROUND 19 — A COLOURWAY IS THE REAL COMBINATION, so it ends any shot that
    // was open. The core's openShot() states this contract from the other side
    // ("applyCap resets shotIndex and repaints, so setting the index before it
    // would be undone a line later") and calls applyCap FIRST for that reason.
    // Measured before this line: a colourway chip after a worn-shot chip left
    // the caption reading "<Plate> as worn on <Cap>" over the composite, and the
    // worn chip still pressed.
    this.shotIndex = -1;
    // ROUND 20 — A COLOURWAY CHANGE ALSO ENDS A CAP-ONLY VIEW, for the same
    // reason it ends an open shot: what is on the stage has to be what the
    // caption says. `_soloKeep` is the one exception — enterSoloCap() sets it
    // while it drives the colourway through the real radio, because that route
    // lands here and would otherwise undo the state it is on its way to set.
    if (!this._soloKeep) this.soloCapKey = null;

    // Supersede any swap still waiting on its image to decode, or it would
    // paint the previous colourway once it resolves.
    this.swapSeq = (this.swapSeq || 0) + 1;

    // Cancel any snap still in flight: it is showing the previous colourway and
    // would otherwise sit on top of the new one until its animation ends.
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      this.pendingSrc = null;
    }

    // ROUND 22 (Sina): the same six-argument derive() the bundle page uses, and
    // the same measured-box geometry feeding it. The stage is now a LANDSCAPE
    // window onto a square composite here too, so `plate_rect` — a percentage of
    // the square — has to be re-expressed as a percentage of the BOX or the snap
    // lands off the plate. vRatio = 1 collapses every term back to the old
    // three-argument arithmetic, which is what makes this safe for the default
    // (uncropped, square) path.
    const sgeo = this.stageGeom();
    const geo = derive(cap.plate || {}, this.settings.thumbAspect, this.settings.clipPadPercent,
                       sgeo.vRatio, sgeo.focus, sgeo.picFrac);
    const s = this.style;
    s.setProperty('--nh-ps-clip', geo.clip);
    s.setProperty('--nh-ps-thumb-size', `${geo.thumb.size.toFixed(2)}%`);
    s.setProperty('--nh-ps-thumb-x', `${geo.thumb.x.toFixed(2)}%`);
    s.setProperty('--nh-ps-thumb-y', `${geo.thumb.y.toFixed(2)}%`);
    // ROUND 18. In bundle mode on a phone the stage is inside the DOCK, which
    // is a sibling of this element — an inherited custom property does not
    // cross that boundary, and the incoming snap layer would fall back to the
    // stylesheet's default clip. paintGeometry() restates all four on the
    // stage itself; on every other layout that is the value it inherited
    // anyway, so it is a no-op there.
    this.capGeom = geo;
    if (this.paintGeometry) this.paintGeometry();
    // What the core's renderTray uses to pick a tray thumbnail when a plate
    // has no artwork of its own. One name, both surfaces.
    this.viewCap = cap.key;

    // ROUND 21 — THE FIELD FOLLOWS THE PICTURE. Keyed to `viewCap`, the
    // DISPLAYED colourway, never to `capKey` or `boughtCap`: looking at a plate
    // on Timmer must tint the zone for Timmer whatever is in the cart. The same
    // line the builder has carried since round 11, and the same method now.
    // `fade` matches the picture's own crossfade, so the two move together.
    if (this.paintGround) this.paintGround({ fade: animate });

    // Changing colourway means the shopper is back on the real combination.
    if (this.inEditorial) {
      this.inEditorial = false;
      // Applying a colourway paints a cap on the stage, so the plate-only state
      // is over. This mirrors exitEditorial() — the two must clear the same
      // flags, and missing this one here left the cap button disabled after
      // choosing a colourway while only the artwork was showing.
      this.inArtView = false;
      this.classList.remove('nh-ps--editorial', 'nh-ps--worn');
    if (this.els.stage) this.els.stage.classList.remove('is-shot-ar');
      if (this.els.stage) this.els.stage.classList.remove('is-shot-ar');
      const own = this.psNote();
      if (own && this.capNoteHTML != null) {
        own.innerHTML = this.capNoteHTML;
        this.capNoteHTML = null;
      }
      this.markRail(-1);
    }

    // ROUND 20 — THE SECONDARY BUTTON FOLLOWS THE CAP BEING BOUGHT, not the one
    // on the stage. Rule 2 of this round: the purchased cap changes only in the
    // "Your cap" control, so looking at another colourway must not quietly
    // re-point "Add cap + plate" at it. Off the card `psBoughtCap()` returns the
    // colourway itself, so the default path is exactly what it was.
    this.syncCapAddToCart(this.psBoughtCap() || cap);
    this.syncPeek(this.currentPlate());

    if (this.els.psCapName) this.els.psCapName.textContent = cap.name || '';
    // "Shown on {cap} …" — the template lives in a data attribute so the copy
    // stays a theme setting and the JS only substitutes.
    const note = this.psNote();
    if (note) {
      const tpl = note.getAttribute('data-nh-cap-note-template') || '';
      const parts = tpl.split('{cap}');
      note.textContent = '';
      parts.forEach((part, i) => {
        note.appendChild(document.createTextNode(part));
        if (i < parts.length - 1) note.appendChild(this.capNameEl(cap));
      });
    }

    const input = this.qs(`[data-nh-cap-input][value="${cssAttr(cap.key)}"]`);
    if (input && !input.checked) input.checked = true;

    // ROUND 19 — EVERY ROUTE TO A COLOURWAY LANDS HERE (a swatch radio, the ‹ ›
    // arrows, stepCap, ?cap=, a gallery chip, a cap card, the dropdown), so this
    // is where the shared panel is repainted rather than in any one of them.
    //
    // ROUND 20 REMOVED THE paintCapButton() CALL THAT USED TO BE HERE, and the
    // removal is the point rather than a tidy-up. Round 19 added it because the
    // dropdown and the colourway were ONE state and a swatch change left the
    // button reading the previous cap. They are two states now: the button
    // names the cap being BOUGHT and only selectCap() moves it, so repainting
    // it from every look would be the very coupling rule 2 forbids. The cap
    // ROW is repainted instead — it is the control that follows the view.
    //
    // markStrip, not renderStrip: the chips themselves have not changed, only
    // which one is pressed.
    this.paintCapRow();
    if (this.els.strip && typeof this.markStrip === 'function') {
      this.markStrip();
      this.paintCapNote();
    }

    this.repaintThumbs();
    this.syncCategories();

    // The plate on screen may have no mockup in the new colourway; fall back to
    // the first one that does rather than showing an empty stage.
    const plates = this.platesFor(this.catIndex, this.capKey);
    if (!plates.length) {
      const nextCat = this.data.categories.findIndex((_, i) => this.platesFor(i, this.capKey).length);
      if (nextCat >= 0) this.selectCategory(nextCat);
      // The colourway still changed, so the bundle variant still has to follow
      // it — this branch returns before the tail below.
      if (this.bundleOn) this.syncBuy();
      return;
    }
    const idx = Math.min(this.plateIndex, plates.length - 1);
    const src = stageSrc(plates[idx], this.capKey);

    if (animate) {
      this.els.base.classList.remove('is-fading');
      // Force a reflow so the class can be re-added and re-trigger the fade.
      void this.els.base.offsetWidth;
      this.els.base.classList.add('is-fading');
    }
    this.setBase(src);
    this.plateIndex = idx;
    this.syncRadio(this.catIndex, plates[idx].id);
    this.paintCaption(plates[idx], false);
    this.syncAddToCart(plates[idx]);
    this.syncDescription(plates[idx]);
    // The colourway moved, so the chip showing "the live combination" is stale.
    this.paintRailCapThumb(plates[idx]);
    // The plate under the swatches may have changed too, if the previous one had
    // no mockup in this colourway and we fell through to another.
    this.paintCapSwatches(plates[idx]);
    // ROUND 18 — the colourway decides the BUNDLE VARIANT.
    // ROUND 20 — IT NO LONGER DOES, and syncBuy() stays here anyway.
    //
    // Round 18 hooked this in because the cap on the stage WAS the cap you
    // bought, so `items[0][id]` went stale the moment the swatch row moved.
    // Rule 2 has since separated the two: boughtCapKey() reads `boughtCap`,
    // which only selectCap() writes, so a look leaves the posted variant
    // exactly where it was. The call stays because syncBuy() also reconciles
    // the chosen plates, the count and the button's own label, and because off
    // the card `boughtCap` is unset and boughtCapKey() falls back to `capKey` —
    // which is the round-18 behaviour, unchanged, for every surface that has
    // no "Your cap" control to move the purchase with.
    if (this.bundleOn) {
      this.syncBuy();
      this.syncSheetTitle();
    }
  }

  /**
   * Hide category tabs that have nothing to show in the current colourway.
   * A tab that opens an empty grid is worse than no tab, and until the mockup
   * matrix is complete a whole category can legitimately be empty for one cap.
   */
  syncCategories() {
    // Stacked has no tabs, so the same rule applies one level out: hide the
    // whole category block, heading and all. A heading over an empty grid is
    // worse than no heading.
    const targets = this.stacked ? this.panels : this.tabs;
    let anyVisible = false;
    targets.forEach((el, i) => {
      const empty = this.platesFor(i, this.capKey).length === 0;
      el.hidden = empty;
      if (!empty) anyVisible = true;
    });
    // Never hide everything: if no category has a mockup for this colourway,
    // leave them alone rather than presenting an empty widget.
    if (!anyVisible) targets.forEach((el) => { el.hidden = false; });
  }

  /**
   * Reconcile the thumbnail grid with the selected colourway.
   *
   * Visibility is decided by whether a STAGE mockup exists for this cap — a
   * tile you can click must lead somewhere. The tile's own picture is separate:
   * plates with their own flat artwork keep it (it is cap-independent), and
   * only the rest are repainted with a crop of the cap mockup.
   */
  repaintThumbs() {
    this.querySelectorAll('[data-nh-plate-thumb]').forEach((el) => {
      let map = el.nhImages;
      if (!map) {
        try {
          map = JSON.parse(el.dataset.nhImages || '{}');
        } catch {
          map = {};
        }
        el.nhImages = map;
      }

      const plate = { images: map, art: el.dataset.nhArt || '' };
      const stage = stageSrc(plate, this.capKey);
      const label = el.closest('[data-nh-plate-src]');

      if (label) {
        // No mockup for this colourway yet — hide the tile so its radio leaves
        // the group and arrow-key navigation skips it.
        label.hidden = !stage;
        if (stage) label.dataset.nhPlateSrc = stage;
      }
      if (!stage || plate.art) return;

      const src = thumbSrc(plate, this.capKey);
      if (src) el.style.setProperty('--nh-ps-thumb-img', `url("${cssUrl(src)}")`);
    });
  }

  /* --------------------------------------------------------------- cart */

  /**
   * Point the add-to-cart form at the plate currently on the stage.
   *
   * The button's accessible name is assembled from real text in DOM order —
   * visible label, visible price, then a visually-hidden product name. That
   * order is required: WCAG 2.5.3 needs the visible text to survive as a
   * contiguous run at the head of the name, so "Add to cart 195 kr" must not be
   * split by the hidden part. aria-label is deliberately not used; a changing
   * aria-label is exactly the construction some screen readers fail to announce.
   */
  /**
   * Rebuild the photo strip for the selected plate.
   *
   * Hidden outright when the plate has no editorial shots — about 15 of 78 —
   * so an empty strip never reserves space. The first item is always "on the
   * cap", i.e. the way back to the live combination.
   */
  /**
   * The rail's images: the plate's worn photos, then the artwork itself.
   *
   * The artwork goes LAST on purpose. The shopper just tapped it in the grid, so
   * leading with it would open onto the picture they were already looking at —
   * on mobile the sheet would slide up to reveal the same image and read as
   * inert. The worn shots are the payoff, so they keep the lead slot.
   *
   * But it belongs in the rail, because it is the only view where the art is not
   * curved over a cap, and the art is the thing being bought.
   *
   * Modelled as one more editorial shot rather than a separate stage mode: it
   * then flows through showEditorial, markRail, openLeadShot and the rail's radio
   * group with no new state. Its `cap` is null because it is cap-independent,
   * which is already the "cannot say which colourway" case the caption handles —
   * it just gets its own label instead of the neutral one.
   */
  /* ROUND 19 — shotsFor() moved to assets/nordenheim-bundle-core.js. The two
     copies were the same list in the same order, field for field, and one of
     them now feeds both the editorial rail here and the shared gallery strip.
     It is installed onto this prototype by installBundleCore at the foot of
     this file, so every call site below is unchanged. */

  syncRail(plate) {
    // ROUND 19 — the SHARED gallery band and caption band follow the plate.
    // Hung here because this is the one method that already runs on every
    // plate change and already computes the shot list they both read. Both are
    // absent unless bundle mode rendered them, so the default path pays for
    // two `typeof` checks and nothing else.
    if (typeof this.renderStrip === 'function' && this.els.strip) {
      this.shotIndex = -1;
      this.renderStrip();
      this.paintCapNote();
    }
    const rail = this.els.rail;
    if (!rail) return;
    const shots = this.shotsFor(plate);
    this.editorial = shots;

    rail.querySelectorAll('[data-nh-worn]').forEach((n) => n.remove());
    if (!shots.length) {
      rail.hidden = true;
      return;
    }
    rail.hidden = false;

    this.paintRailCapThumb(plate);
    this.paintCapSwatches(plate);

    const labels = this.settings.labels || {};
    const groupName = (this.qs('[data-nh-cap-input]') || {}).name || `nh-worn-${this.id || 'x'}`;

    shots.forEach((shot, i) => {
      const capName = shot.cap ? (this.cap(shot.cap) || {}).name : null;
      let text;
      if (shot.art) {
        text = labels.art || 'shown on its own';
      } else if (capName) {
        text = `${labels.editorial || 'as worn on'} ${capName}`;
      } else {
        // No colourway recorded for this shot. The radio still needs an
        // accessible name, but it must not invent a relationship — so it falls
        // back to the plate itself rather than asserting how it is being worn.
        const plain = (labels.editorialPlain || '').trim();
        text = plain ? `${plain} ${i + 1}` : `${(plate && plate.name) || ''} ${i + 1}`.trim();
      }

      // Same shape as a colourway swatch, and crucially the same radio `name`,
      // so the browser treats the whole row as ONE group: arrow keys traverse
      // it, it announces "3 of 11", and checking any member unchecks the rest.
      // As buttons these sat outside the group and the keyboard walked past them.
      const label = document.createElement('label');
      label.className = 'nh-ps__cap nh-ps__cap--worn';
      label.setAttribute('data-nh-worn', String(i));

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'nh-ps__sr';
      input.name = groupName;
      input.value = `worn:${i}`;
      input.setAttribute('data-nh-worn-input', String(i));

      const face = document.createElement('span');
      face.className = 'nh-ps__cap-face has-image' + (shot.art ? ' nh-ps__cap-face--art' : '');
      face.style.backgroundImage = `url("${cssUrl(shot.thumb)}")`;

      const sr = document.createElement('span');
      sr.className = 'nh-ps__sr';
      sr.textContent = text;

      label.append(input, face, sr);
      this.on(input, 'change', () => {
        if (!input.checked) return;
        // Same for a worn shot: hold that slot across plates. Programmatic
        // checks do not fire `change`, so this only ever records a real click
        // or a real arrow-key move.
        // The artwork is recorded by KIND, not by index: it is always the last
        // slot, but that index differs per plate (some have two worn shots, some
        // five). Storing 'art' keeps the choice on the artwork across plates,
        // which is the same promise the colourway and worn slots make.
        this.userPick = shot.art ? { type: 'art' } : { type: 'worn', index: i };
        // Colourway first: applyCap repaints the stage and clears editorial
        // state, so doing it after would wipe the photo. It does not touch the
        // radios, so this input stays checked.
        if (shot.cap && shot.cap !== this.capKey) this.applyCap(shot.cap, { animate: false });
        this.showEditorial(i);
      });
      rail.appendChild(label);
    });

    // What this plate should open on. Opening cannot happen here: selectPlate
    // paints the stage AFTER this returns (snapTo), so anything set now is
    // overwritten a frame later. Record the intent; selectPlate acts on it.
    //
    //   no choice yet  -> the lead worn shot, so the plate is seen in context
    //   picked a cap   -> nothing; stay on that colourway's mockup
    //   picked the art -> the artwork again, which is always the last slot
    //   picked a worn  -> the same slot on the new plate, clamped, since plates
    //                     carry different numbers of shots
    //
    // The clamp counts WORN shots, not rail slots. Counting slots would let a
    // high worn index on a five-shot plate fall onto the artwork of a two-shot
    // one, silently turning "keep showing me a model" into "show me flat art".
    const pick = this.userPick;
    const worn = shots.filter((s) => !s.art).length;
    if (!shots.length || (pick && pick.type === 'cap')) {
      this.leadPending = null;
      this.markRail(-1);
    } else if (pick && pick.type === 'art') {
      this.leadPending = shots.length - 1;
    } else if (pick && pick.type === 'worn' && worn) {
      this.leadPending = Math.min(pick.index, worn - 1);
    } else if (worn) {
      this.leadPending = 0;
    } else {
      // Artwork and nothing else. The cap mockup is the better opener: it shows
      // the plate in context, which a flat artwork by definition cannot.
      this.leadPending = null;
      this.markRail(-1);
    }
  }

  /**
   * The "back" chip wears the LIVE combination, so the strip reads as views of
   * one plate rather than a filmstrip with an odd first frame.
   *
   * Repainted on both axes. It was originally set only when the plate changed,
   * which left it showing whichever colourway happened to be current at that
   * moment — switch cap without switching plate and the chip still showed the
   * old cap while the stage showed the new one.
   */
  paintRailCapThumb(plate) {
    const btn = this.els.railCap;
    if (!btn) return;
    const p = plate || this.currentPlate();
    const img = p && (p.images || {})[this.capKey];
    if (img && img.thumb) btn.style.backgroundImage = `url("${cssUrl(img.thumb)}")`;
  }

  /**
   * Put the current plate on every colourway swatch.
   *
   * Plate-driven, not cap-driven: the row shows THIS plate on each cap, so it
   * repaints when the plate changes and is untouched when the colourway does.
   * A colourway with no mockup keeps its colour dot rather than going blank.
   */
  paintCapSwatches(plate) {
    const p = plate || this.currentPlate();
    if (!p) return;
    const images = p.images || {};
    this.qsa('[data-nh-cap-face]').forEach((face) => {
      const img = images[face.getAttribute('data-nh-cap-face')];
      if (img && img.thumb) {
        face.style.backgroundImage = `url("${cssUrl(img.thumb)}")`;
        face.classList.add('has-image');
      } else {
        face.style.backgroundImage = '';
        face.classList.remove('has-image');
      }
    });
  }

  currentPlate() {
    const plates = this.platesFor(this.catIndex, this.capKey);
    return plates[Math.min(this.plateIndex, plates.length - 1)] || null;
  }

  /**
   * The cap's name as a link to its product, or bold text when the cap block
   * points at nothing — a dead <a> is worse than plain text.
   *
   * Shared so the worn-shot line renders the name exactly like the "Shown on"
   * line does. They were built separately and the worn one wrote plain text, so
   * the same cap was a link in one state and not in the other.
   */
  capNameEl(cap) {
    const el = document.createElement(cap && cap.url ? 'a' : 'b');
    const name = (cap && cap.name) || '';
    if (cap && cap.url) {
      el.setAttribute('href', cap.url);
      el.className = 'nh-ps__namelink';
      // The text goes in a span so the underline can stop before the arrow.
      const t = document.createElement('span');
      t.textContent = name;
      el.appendChild(t);
    } else {
      el.textContent = name;
    }
    return el;
  }

  /**
   * Open the plate's first worn shot, once the stage has finished being painted.
   *
   * Lead with the plate in context: on a head rather than on a bare mockup. The
   * shot carries its colourway, so the swatch, the "shown on" line, the price
   * and the cap button all move with it and stay truthful. Plates with no worn
   * shots simply keep the live cap view.
   *
   * Must run AFTER selectPlate paints — showEditorial bumps swapSeq and clears
   * `pending`, which is what stops the in-flight snap from committing the mockup
   * back over the photo.
   */
  openLeadShot() {
    const i = this.leadPending;
    this.leadPending = null;
    if (i == null) return;
    const shot = (this.editorial || [])[i];
    if (!shot) return;
    if (shot.cap && shot.cap !== this.capKey) this.applyCap(shot.cap, { animate: false });
    this.showEditorial(i);
  }

  /**
   * Reflect the open view in the radio group. Only one member can be checked,
   * which is exactly the constraint wanted here and the browser enforces it.
   * index < 0 means "no photo open", so the live colourway takes the check back.
   */
  markRail(index) {
    const rail = this.els.rail;
    if (!rail) return;
    if (index < 0) {
      const capInput = this.qs(`[data-nh-cap-input][value="${cssAttr(this.capKey)}"]`);
      if (capInput && !capInput.checked) capInput.checked = true;
      return;
    }
    const worn = rail.querySelector(`[data-nh-worn-input="${index}"]`);
    if (worn && !worn.checked) worn.checked = true;
  }

  /**
   * Show one of the plate's own photos on the stage.
   *
   * These are NOT tied to a colourway — nothing records which cap a model is
   * wearing — so while one is open the section is put in an explicit editorial
   * state: the "Shown on <cap>" line is replaced and the colourway row is
   * visually inert. Without that the picture would silently assert a
   * combination that may not be true.
   */
  showEditorial(i) {
    const shot = (this.editorial || [])[i];
    const base = this.els.base;
    if (!shot || !base) return;

    // Cancel any snap in flight, or it repaints over the photo when it lands.
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      this.pendingSrc = null;
    }
    this.swapSeq = (this.swapSeq || 0) + 1;

    this.inEditorial = true;
    // Only the artwork tile has no cap in frame; a worn photo still names one.
    this.inArtView = !!shot.art;
    this.syncCapAddToCart(this.psBoughtCap());
    this.classList.add('nh-ps--editorial');
    // ROUND 22.2 — a worn photograph (a model shot) takes the bundle page's
    // treatment: the stage becomes the photo's own shape and the picture is
    // contained (builder round 14, `is-shot-ar`). Artwork and packaging keep
    // the composite's box.
    const worn = !shot.art && !shot.packaging;
    this.classList.toggle('nh-ps--worn', worn);
    if (this.els.stage) {
      const st = this.els.stage;
      if (worn) {
        const ar = (shot.w > 0 && shot.h > 0) ? shot.w / shot.h : 1;
        st.style.setProperty('--nh-bb-shot-ar', String(Number(ar.toFixed(4))));
      }
      st.classList.toggle('is-shot-ar', worn);
    }
    this.setBase(shot.full, { bleed: false });

    const labels = this.settings.labels || {};
    const note = this.psNote();
    if (note) {
      if (this.capNoteHTML == null) this.capNoteHTML = note.innerHTML;
      // Always the editorial label, never the media alt. The alt on these files
      // is usually just the product title, which reads as a caption for the
      // picture and quietly loses the one thing this line exists to say: that
      // what you are looking at is NOT the selected colourway.
      // Now that a shot's colourway is known for the common cases, say it. The
      // neutral label is reserved for shots we could not attribute — asserting
      // the wrong cap would be worse than staying vague.
      // "As worn on <cap>" so it parallels the "Shown on <cap>" line. When the
      // colourway is unknown the preposition would dangle, so a separate plain
      // label is used instead of trying to trim one string into the other.
      const shotCap = shot.cap ? this.cap(shot.cap) : null;
      note.textContent = '';
      if (shot.art) {
        // The one view with no cap in it, so it needs its own tail rather than
        // the "shown on <cap>" one.
        note.appendChild(document.createTextNode(labels.art || '— plate only'));
      } else if (shotCap) {
        // Same wording as a mockup. A photo of someone wearing it is still the
        // plate shown on that colourway, and a second phrasing for the same
        // relationship just made the strip read as two different things.
        note.appendChild(document.createTextNode((labels.editorial || 'shown on') + ' '));
        note.appendChild(this.capNameEl(shotCap));
      } else if ((labels.editorialPlain || '').trim()) {
        note.appendChild(document.createTextNode(labels.editorialPlain));
      }
    }
    this.markRail(i);
    // ROUND 19 — THE SHARED CAPTION AND THE GALLERY BAND HAVE TO AGREE WITH THE
    // PICTURE, whichever route got here.
    //
    // There are four: a chip in the gallery band (openShot, which sets this
    // already), a worn radio in the editorial rail, `openLeadShot`, and — the
    // one that caught it — the ‹ › arrows, whose stepCap() walks the whole radio
    // GROUP, and the rail's worn radios are deliberately in that group. Stepping
    // past the last colourway therefore lands on a worn photograph. Measured
    // before this: a worn shot on the stage under a caption still reading
    // "<Plate> shown on <Cap>", because `shotIndex` had not moved.
    //
    // Set here rather than in each caller, so a fifth route cannot miss it.
    if (this.els.strip) {
      this.shotIndex = i;
      this.paintCapNote();
      this.markStrip();
    }
  }

  exitEditorial({ repaint = true } = {}) {
    if (!this.inEditorial) {
      this.markRail(-1);
      return;
    }
    this.inEditorial = false;
    this.inArtView = false;
    this.syncCapAddToCart(this.psBoughtCap());
    this.classList.remove('nh-ps--editorial', 'nh-ps--worn');
    const note = this.psNote();
    if (note && this.capNoteHTML != null) {
      note.innerHTML = this.capNoteHTML;
      this.capNoteHTML = null;
    }
    this.markRail(-1);
    // ROUND 19 — back to the real combination, so the shared caption and the
    // pressed chip say so. applyCap() below repaints both; this covers the
    // `repaint: false` call sites too.
    if (this.els.strip) {
      this.shotIndex = -1;
      this.paintCapNote();
      this.markStrip();
    }
    // Put the real combination back under the shopper.
    if (repaint) this.applyCap(this.capKey, { animate: false });
  }

  syncAddToCart(plate) {
    const { atcButton, atcVariant, atcLabel, atcPrice, atcName, atcWrapper } = this.els;
    if (!atcButton || !plate) return;

    const labels = this.settings.labels || {};
    const available = plate.available !== false;
    const variantId = plate.variantId;

    if (atcVariant) atcVariant.value = variantId == null ? '' : String(variantId);
    // A disabled field is omitted from FormData, so ordinary plates send nothing.
    if (this.els.atcPreorder) this.els.atcPreorder.disabled = !plate.preorder;
    if (atcPrice) atcPrice.textContent = available && plate.price ? plate.price : '';
    // Leading comma so it reads as a continuation, not a new sentence.
    if (atcName) atcName.textContent = plate.name ? `, ${plate.name}` : '';

    if (atcLabel) {
      let text = labels.addToCart || 'Add to cart';
      if (!available) text = labels.soldOut || 'Sold out';
      else if (plate.preorder) text = labels.preorder || 'Pre-order';
      atcLabel.textContent = text;
    }

    // The theme only honours the disabled ATTRIBUTE, so aria-disabled would let
    // a sold-out plate through.
    const blocked = !available || variantId == null;
    atcButton.disabled = blocked;
    if (atcWrapper) {
      // The theme's own flag for "leave this button disabled"; without it the
      // button is re-enabled 1000ms after any add.
      atcWrapper.classList.toggle('variant--soldout', blocked);
    }

    // A new selection invalidates whatever the last add said.
    this.psClearAddStatus();
  }

  /**
   * Show the selected plate's own description.
   *
   * This is merchant HTML from this shop — the same value the product page
   * renders — so it is inserted as markup rather than text. It is deliberately
   * NOT a live region: re-reading a full description on every arrow-key press
   * through the plate grid would make the widget unusable with a screen reader,
   * and the caption already announces which plate is selected.
   */
  syncDescription(plate) {
    const el = this.els.description;
    if (!el) return;
    const html = (plate && plate.description) || '';
    if (el.dataset.nhCurrent === String(plate && plate.id)) return;
    el.dataset.nhCurrent = String(plate && plate.id);
    el.innerHTML = html;
    el.hidden = html.trim() === '';
  }

  psOnAddToCart(e, isCap) {
    const btn = isCap ? this.els.capAtcButton : this.els.atcButton;
    if (!btn || btn.disabled) {
      e.preventDefault();
      return;
    }
    // Without the theme's cart JS (e.g. the local preview harness) let the form
    // submit natively rather than swallowing the click.
    if (!window.theme || !window.theme.routes) return;

    e.preventDefault();
    // The theme restores focus here when the cart drawer closes.
    window.a11y = window.a11y || {};
    window.a11y.lastElement = btn;
    document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: { button: btn } }));
  }

  psAnnounceAdd(ok, event, isCap) {
    const atcStatus = isCap ? this.els.capAtcStatus : this.els.atcStatus;
    const atcError = isCap ? this.els.capAtcError : this.els.atcError;
    const labels = this.settings.labels || {};
    if (ok) {
      if (atcError) atcError.textContent = '';
      if (atcStatus) atcStatus.textContent = labels.added || 'Added to your cart.';
      return;
    }
    if (atcStatus) atcStatus.textContent = '';
    if (!atcError) return;
    const res = event && event.detail && event.detail.response;
    atcError.textContent =
      (res && (res.description || res.message)) || 'Sorry, that could not be added to your cart.';
  }

  psClearAddStatus() {
    if (this.els.atcStatus) this.els.atcStatus.textContent = '';
    if (this.els.atcError) this.els.atcError.textContent = '';
  }

  clearCapAddStatus() {
    if (this.els.capAtcStatus) this.els.capAtcStatus.textContent = '';
    if (this.els.capAtcError) this.els.capAtcError.textContent = '';
  }

  /**
   * Point the secondary button at the cap colourway currently on the stage.
   *
   * Mirrors syncAddToCart: same span-order contract for the accessible name,
   * same reliance on the disabled ATTRIBUTE (the theme ignores aria-disabled),
   * and the same .variant--soldout flag that stops the theme re-enabling a
   * button it should not. A colourway with no linked cap product has a null
   * variantId and stays disabled rather than posting an empty id.
   */
  syncCapAddToCart(cap) {
    const els = this.els;
    const btn = els.capAtcButton;
    if (!btn || !cap) return;

    const labels = this.settings.labels || {};
    const variantId = cap.variantId;
    const available = cap.available !== false && variantId != null;

    if (els.capAtcVariant) els.capAtcVariant.value = variantId == null ? '' : String(variantId);

    // Second line item: the plate currently on the stage. Enabled only when it
    // is genuinely buyable, since a disabled field is omitted from FormData and
    // the add degrades to cap-only rather than failing.
    const plate = this.currentPlate();
    const plateOk = !!(plate && plate.variantId != null && plate.available !== false);
    if (els.capAtcPlate) {
      els.capAtcPlate.value = plateOk ? String(plate.variantId) : '';
      els.capAtcPlate.disabled = !plateOk;
    }
    if (els.capAtcPlateQty) els.capAtcPlateQty.disabled = !plateOk;
    if (els.capAtcPrice) els.capAtcPrice.textContent = available && cap.price ? cap.price : '';
    if (els.capAtcName) els.capAtcName.textContent = cap.name ? `, ${cap.name}` : '';
    if (els.capAtcLabel) {
      els.capAtcLabel.textContent = available
        ? labels.capAddToCart || 'Add cap + plate'
        : labels.soldOut || 'Sold out';
    }

    // The plate-only view is the one state with no cap on screen. A colourway is
    // still held internally — the last one chosen — so the button WOULD add a
    // real cap, just not one the shopper can currently see. Adding 795 kr of cap
    // that is nowhere on screen is the wrong kind of surprise, so the offer is
    // withdrawn until a cap is visible again. Every other state, including a worn
    // photo, has a known colourway and stays buyable.
    const noCapOnScreen = this.inArtView === true;
    btn.disabled = !available || noCapOnScreen;
    // `disabled` alone removes it from the accessibility tree, so the reason
    // would vanish with it. aria-disabled keeps it announced.
    btn.setAttribute('aria-disabled', String(btn.disabled));
    if (els.capAtcWrapper) {
      els.capAtcWrapper.classList.toggle('variant--soldout', !available);
      els.capAtcWrapper.classList.toggle('nh-ps__atc--nocap', noCapOnScreen && available);
    }

    this.clearCapAddStatus();
  }

  /* ------------------------------------------------------------ preloading */


  /* =========================================================== BUNDLE MODE

     ROUND 18 — Mix & Match becomes bundle-capable IN PLACE. Choosing Trio or
     Collector in the picker turns this same section into the bundle builder
     without leaving the page; choosing Starter or Plates only turns it back.

     Everything below is the HOST ADAPTER. The tray, the chosen set, the
     count, the dynamic button, the cart payload, the toast and the phone Tray
     Sheet are NOT here — they are assets/nordenheim-bundle-core.js, the same
     methods the bundle PDPs run, copied onto this prototype at the foot of
     this file. What is here is the dozen things the core asks THIS surface
     for: where its root is, how to resolve a plate reference, which cap is
     being bought, and which of its nodes move into the dock.

     Nothing in here runs, and nothing is rendered, while `bundle_inplace` is
     off. That is the whole safety story: the section's default behaviour is
     not gated behind a flag, it is simply untouched.
     ---------------------------------------------------------------------- */

  /** The element that carries `nh-bb--sheet`, `nh-ps--bundle` and the pixels
   *  the core writes (`--nh-bb-peek-h`, `--nh-bb-bar-w`). The SECTION, not
   *  this custom element: the dock is its sibling, one level up, and the end
   *  sentinel's reserved-seat margin is keyed to an ancestor of both. */
  bcRoot() {
    return this.closest('.nh-ps') || this;
  }

  /** Is the dock the phone layout for this instance? A setting (`inline`
   *  renders no dock at all — the tray simply rides inside this section's own
   *  sheet) plus the dock actually being in the markup. */
  dockMode() {
    return !!(this.els && this.els.dock && this.els.sheet)
      && this.settings.bundlePhoneMode !== 'inline';
  }

  /** The core's gate for every Tray Sheet path. */
  sheetActive() {
    // ROUND 22.1 — THE PHONE DOCK SERVES THE FREE TRAY TOO. It was gated on
    // `bundleOn`, which was every mode a phone shopper met until today; now the
    // default is the free tray, `bundleOn` is false, and a phone fell back to
    // this section's OWN sheet — where the deck (tray, nudge, buy button) sits
    // inside a sheet that only a plate tap opens. Measured at 375×812: the buy
    // button 0×0, the tray off-screen, a cap and a plate chosen with no way to
    // pay for them. The dock — the Tray Sheet Sina approved in rounds 15–16 —
    // is the phone layout for anything with a tray, so that is the test now.
    return !!(this.bundleOn || this.trayGrows) && this.dockMode() && this.compactWidth();
  }

  compactWidth() {
    if (this.mq) return !!this.mq.matches;
    return window.matchMedia('(max-width: 900px)').matches;
  }

  /**
   * THE PLATE REFERENCE IS A RAW PAYLOAD INDEX, on both surfaces.
   *
   * `platesFor(ci, capKey)` returns the plates that have a mockup for the
   * CURRENT colourway, and `selectPlate` indexes into that filtered list while
   * every tile's `data-index` is an index into the unfiltered one. Those two
   * diverge for any plate with a mockup on some colourways and not others —
   * a pre-existing quirk of this section, deliberately NOT fixed here because
   * fixing it touches the default path.
   *
   * The cart can never inherit it: `previewed` below is read from the radio
   * that `syncRadio` actually CHECKED, so its `pi` names the same product this
   * function returns, whatever `selectPlate` was passed. Stage and tray agree
   * by construction rather than by arithmetic.
   */
  plateAt(ci, pi) {
    const cat = this.data.categories[ci];
    return (cat && cat.plates && cat.plates[pi]) || null;
  }

  /** What the stage is showing, as a raw {ci, pi}. Written by syncRadio. */
  get previewed() {
    return this._previewed || null;
  }

  /** The core's one way to change what is on the stage: CLICK THE REAL RADIO.
   *  Calling selectPlate directly would skip the category pill, the roving
   *  tabindex, the caption, the rail and the URL sync — the lesson
   *  restoreFromUrl already records. */
  preview(ref) {
    if (!ref) return;
    const r = this.querySelector(
      `[data-nh-plate-input][data-cat="${cssAttr(ref.ci)}"][data-index="${cssAttr(ref.pi)}"]`
    );
    if (r) r.click();
  }

  /**
   * THE CAP BEING BOUGHT, resolved to the ACTIVE BUNDLE'S variant.
   *
   * The core's syncBuy() reads `variantId`, `price`, `name` and `available`
   * off whatever this returns and posts it as `items[0]` — so on this surface
   * the "cap" it is handed is the bundle variant for the colourway on the
   * stage, which is exactly what a Trio in Björk is.
   *
   * `bundle_require_cap_choice` is off by default and the recommendation is to
   * leave it off: the swatch row is a radio group with one always checked, so
   * asking the shopper to "pick" a cap they can already see selected is a
   * phantom step, and a null state in a radio group means either unchecking
   * every radio (which changes the default path) or a second "has it been
   * touched" flag the default path has to ignore — two ways for the picture
   * and the purchase to get out of step. On, the bundle PDP's round-7 model
   * applies here too and the button reads "Pick your cap …" until one is.
   */
  boughtCapKey() {
    if (this.settings.bundleRequireCapChoice) return this.boughtCap || null;
    // ROUND 20 — THE CAP BEING BOUGHT IS NO LONGER THE CAP ON THE STAGE.
    //
    // Rule 2: only the "Your cap" control moves it. `boughtCap` is seeded in
    // buildBundle() from whatever colourway the page opened on (which is what
    // ?cap= has already set by then, so a shared link still buys what it shows)
    // and is written by exactly one method, selectCap().
    //
    // The fallback is not decoration: `boughtCap` is only ever set while the
    // bundle panel is rendered, so with `bundle_inplace` off this returns
    // `capKey` and every cart on the default path is byte-for-byte what it was.
    return this.boughtCap || this.capKey || null;
  }

  /** The cap object being bought, for the two buttons that post it. */
  psBoughtCap() {
    return this.cap(this.boughtCapKey()) || this.cap(this.capKey) || null;
  }

  /** ROUND 22 — the cap for "Cap + plates": the Art Cap product, not a bundle
   *  variant. `bundleRequireCapChoice` still decides whether a cap has to be
   *  picked deliberately or the displayed one counts, so the two modes cannot
   *  disagree about what "picked" means. */
  freeCap() {
    // ROUND 22 — a cap can now be taken OUT of the tray, so "none" has to be a
    // state of its own. boughtCapKey() falls back to the DISPLAYED colourway
    // when nothing was picked deliberately, which is right for showing and
    // wrong for buying once removing is possible.
    const inTray = this.capsInTray ? this.capsInTray() : [];
    if (!inTray.length) {
      // ROUND 22.2 — cleared HERE too. A cap removed after the tray had matched
      // a bundle left `freeBundle` pointing at the Trio, and cartBundleHandle()
      // then told the clash guard a plates-only add was a bundle: three loose
      // plates were refused against a Collector already in the cart.
      this.partName = '';
      this.freeBundle = null;
      return null;
    }
    const cap = inTray[0];
    const key = cap.key || this.boughtCapKey();
    if (!cap) return null;
    // ROUND 22 — THE TRAY DECIDES WHAT IS POSTED (Sina, 2026-09-04: "why do we
    // have to decide beforehand… let them mix and match freely").
    //
    // A tray holding a cap and exactly a bundle's plate count IS that bundle, so
    // items[0] becomes the bundle product's variant for this colourway and the
    // plate lines carry its name. Every other count posts the cap itself and
    // lets the automatic tier discounts price it. Always the cheaper of the two
    // — checked against real carts at every count, not assumed:
    //   cap+1 695 · cap+2 890 · cap+3 995 (Trio) · cap+4 1190 ·
    //   cap+5 1245 (Collector) · cap+6 1470
    // This is also what keeps the one-BXGY-per-order rule out of the shopper's
    // way: a bundle is never posted ALONGSIDE loose plates, only instead of them.
    const n = this.chosen.length;
    // ONE cap only: a bundle product is one cap and its plates, so a two-cap
    // tray is never a Trio however many plates it holds.
    // ROUND 23 (ladder v2, 2026-09-05) — not "exactly a bundle's count" any
    // more: the CHEAPEST way to buy this tray. See pickBundle().
    const b = inTray.length === 1 ? this.pickBundle(n, key, cap) : null;
    const v = b && b.variants ? b.variants[key] : null;
    if (v && v.id != null && v.available !== false) {
      this.partName = b.shortName || b.name || '';
      this.freeBundle = b;
      return { key, name: cap.name || key, variantId: v.id, price: v.price || '', available: true };
    }
    this.partName = '';
    this.freeBundle = null;
    return {
      key,
      name: cap.name || key,
      variantId: cap.variantId != null ? cap.variantId : null,
      price: cap.price || '',
      available: cap.available !== false,
    };
  }

  /** The tray's price, in minor-unit-free integers, and the same number the
   *  cart charges — verified at every count rather than trusted:
   *  695 / 890 / 995 / 1190 / 1245 / 1470.
   *
   *  A bundle's own price wins when the tray IS that bundle. Otherwise: the cap
   *  at list, one plate free with it, and the rest at whichever tier the TOTAL
   *  plate count earns. The three tier prices are settings, so a discount change
   *  is a theme-editor edit and not a code one. */
  /** The cap card's pill: adds this colourway to the tray, or takes the current
   *  one out. Routed through selectCap() so the "Your cap" control, the caption
   *  and the ground all move the way they do for any other cap choice. */
  toggleCapAdd(key) {
    if (!key) return;
    // ROUND 22 — A CAP IS A TRAY ITEM (Sina: "have the cap not take its own row
    // any longer but rather be in the grid of the added plates… this way we can
    // add multiple caps as well").
    //
    // Measured before building, because it decides whether the feature is worth
    // anything: two caps and two plates costs 1 390 — BOTH plates free. The
    // cap's own "buy a cap, get a plate" applies PER CAP, unlike the Trio and
    // Collector discounts, of which an order gets exactly one.
    if (!Array.isArray(this.capsChosen)) this.capsChosen = [];
    // A cap takes a posted row like anything else; refuse past the form's
    // capacity rather than post fewer lines than the button promises.
    if (this.trayItems().length >= this.trayCapacity()) {
      const msg = this.labels.full ? bcFill(this.labels.full, { total: this.trayCapacity() }) : '';
      if (msg) this.say(msg, true);
      return;
    }
    // ROUND 22 (Sina: "we want to be able to add more caps when we want to").
    // The pill ADDS, every time — it is not a toggle. Two of one colourway is a
    // real basket (a pair, a gift), and a control that removed on the second tap
    // made that impossible to express. Removal lives on the tray chip's ×, which
    // is where removal lives for plates too.
    this.capsChosen.push(key);
    if (this.selectCap) this.selectCap(key);
    this.capCleared = false;
    this.paintCapAdds();
    if (this.afterChange) this.afterChange();
  }

  /** The caps in the tray, in the order they were added. */
  capsInTray() {
    return (this.capsChosen || []).map((k) => this.cap(k)).filter(Boolean);
  }

  /**
   * ROUND 22 — THE CAPS SIT IN THE TRAY, in the same grid as the plates (Sina:
   * "have the cap not take its own row any longer but rather be in the grid of
   * the added plates").
   *
   * Built in JS and prepended to the tray, because the tray's own slots are
   * server-rendered from a fixed count and a cap is not one of them. Rebuilt
   * whole on every change: a handful of nodes, and it cannot drift out of step
   * with `capsChosen` the way a patched list would.
   */
  /**
   * ROUND 22 (Sina: "'3 av 4 valda' är konstigt.. vi vet ju inte").
   *
   * He is right, and it was a leak: in a free tray `max` is "what is chosen plus
   * one empty slot", an implementation detail, so the counter promised a total
   * nobody had chosen and the heading promised "välj valfria 3" when nothing was
   * fixed at three. Both go. What is left is the green line, which says the one
   * thing that IS known — what the next plate costs.
   *
   * The counter is only HIDDEN, never emptied: it is the tray's aria-live
   * region, and a screen reader still hears the count change.
   */
  paintFreeTrayHead() {
    const grows = !!this.trayGrows;
    // ROUND 22.1 — THE MODE CLASSES FOLLOW THE STATE, on every repaint. They
    // were written only by setPlainRow(), which the cold path never calls: the
    // page came up with `nh-ps--mirror` still on (server-rendered) and no
    // `nh-ps--free`, so `.nh-ps--card:not(.nh-ps--bundle) .nh-bb__buy` hid the
    // buy button in the phone bar and the mirror rule hid every slot's ×.
    // Measured at 375×812: a cap and two plates chosen, button 0×0.
    const root = this.bcRoot();
    root.classList.toggle('nh-ps--free', !!this.freeMode);
    root.classList.toggle('nh-ps--plates-only', !!this.platesMode);
    if (grows) root.classList.remove('nh-ps--mirror');
    const title = this.els.trayTitle;
    if (title && grows && this.labels.freeTrayTitle) {
      title.textContent = this.labels.freeTrayTitle;
    }
    this.bcRoot().classList.toggle('nh-ps--freehead', grows);
  }

  paintCapChips() {
    // Through the tray block, not a section-scoped query: on a phone moveBuy()
    // carries the block into the dock, which is OUTSIDE this element, and a
    // `this.qs` found nothing — no cap chips on any phone.
    const block = this.els.trayBlock || this;
    const tray = block.querySelector ? block.querySelector('.nh-bb__tray') : null;
    if (!tray) return;
    [...tray.querySelectorAll('[data-nh-ps-capchip]')].forEach((n) => n.remove());
    const caps = this.capsInTray();
    for (let i = caps.length - 1; i >= 0; i -= 1) {
      const cap = caps[i];
      const li = document.createElement('li');
      li.className = 'nh-bb__slot is-filled nh-ps__capchip';
      li.setAttribute('data-nh-ps-capchip', String(i));
      // ROUND 22 (Sina: "varför kan man klicka på en plåt för att se den men
      // inte på en keps?"). The chip's picture is a button, exactly as the plate
      // slot's is: it puts that cap on the stage. Preview, never purchase — the
      // tray is what is bought, and clicking a chip must not change it.
      const thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'nh-bb__slot-thumb nh-ps__capchip-view';
      const media = (cap.media && cap.media[0]) || null;
      if (media && media.thumb) thumb.style.backgroundImage = `url("${media.thumb}")`;
      const viewName = document.createElement('span');
      viewName.className = 'nh-ps__sr';
      viewName.textContent = cap.name || cap.key || '';
      thumb.appendChild(viewName);
      this.on(thumb, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.enterSoloCap) this.enterSoloCap(cap.key);
        else if (this.selectCap) this.selectCap(cap.key);
      });
      const kill = document.createElement('button');
      kill.type = 'button';
      kill.className = 'nh-bb__slot-remove nh-ps__capchip-x';
      kill.innerHTML = '<span class="nh-ps__sr"></span>';
      kill.querySelector('span').textContent =
        bcFill(this.labels.removeLabel || 'Remove {name}', { name: cap.name || cap.key });
      this.on(kill, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.capsChosen.splice(i, 1);
        this.capCleared = this.capsChosen.length === 0;
        this.paintCapAdds();
        this.afterChange();
      });
      li.appendChild(thumb);
      li.appendChild(kill);
      tray.insertBefore(li, tray.firstChild);
    }
    // ROUND 24.2 (Sina, 2026-09-07: "why when choose a bundle do we not have a
    // placeholder for adding a cap but we do for the plates?").
    //
    // Because the plates have server-rendered <li> slots and the cap has never
    // had one — the note above this method says so. In a free tray that is
    // invisible: 22.1 hides the empty plate slot too, so neither has a box. Pick
    // a Trio and the plate slots come back while the cap still has none, and the
    // row reads as three empty boxes with a fourth missing. So build the missing
    // box, in the same place the chip goes.
    //
    // It NAVIGATES to the cap row, like round 24's nudge. A tray tile that
    // silently chose a colourway would be the same mistake 22.1 took out of the
    // nudge: the tray is what is BOUGHT, and nothing may put something in it on
    // the shopper's behalf.
    //
    // No `is-filled`, deliberately — `.nh-ps--freehead .nh-bb__slot:not(.is-filled)`
    // then hides this exactly as it hides the empty plate slot, so the free tray
    // stays box-free without a mode test here and the two can never disagree.
    if (!caps.length) {
      const li = document.createElement('li');
      li.className = 'nh-bb__slot nh-ps__capslot';
      li.setAttribute('data-nh-ps-capchip', 'empty');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nh-bb__slot-thumb nh-ps__capslot-btn';
      // ROUND 24.2b (Sina: "perhaps instead have our cap mockup, small with plus
      // on the upper edge?"). The empty dashed box with a centred "+" said "a
      // thing goes here"; the cap itself says WHICH thing. Drawn small and
      // inside the dashed frame — `background-size` well under cover — so the
      // tile still reads as open. A filled chip covers its whole tile, and these
      // two must never look alike.
      //
      // The cap ON THE STAGE (`capKey`), not a fixed first colourway: the box
      // shows what the shopper is currently looking at, so the "+" is an offer
      // to add THAT. Falls back to the first cap before anything is staged.
      const capsData = (this.data && this.data.caps) || [];
      const shown = this.cap(this.capKey) || capsData[0] || {};
      const media = (shown.media && shown.media[0]) || null;
      if (media && media.thumb) btn.style.backgroundImage = `url("${media.thumb}")`;
      const sr = document.createElement('span');
      sr.className = 'nh-ps__sr';
      // `capPlaceholder` is the EXISTING "Pick your cap" setting the buy button
      // already uses, so this ships with its Swedish and adds no new key.
      sr.textContent = this.labels.capPlaceholder || 'Pick your cap';
      btn.appendChild(sr);
      // Top-right, on the same 13px grid as the chip's × — so the badge does not
      // jump when the cap goes in, it just changes from + to ×.
      const plus = document.createElement('span');
      plus.className = 'nh-ps__capslot-plus';
      plus.setAttribute('aria-hidden', 'true');
      btn.appendChild(plus);
      this.on(btn, 'click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.revealCaps) this.revealCaps();
      });
      li.appendChild(btn);
      tray.insertBefore(li, tray.firstChild);
    }
  }

  /** Every cap card's pill, painted from one state. */
  paintCapAdds() {
    const keys = this.capsChosen || [];
    this.qsa('[data-nh-bb-cap-add]').forEach((btn) => {
      const key = btn.getAttribute('data-nh-bb-cap-add');
      const cap = this.cap(key) || {};
      const on = keys.indexOf(key) >= 0;
      const count = keys.filter((k) => k === key).length;
      btn.classList.toggle('is-chosen', on);
      const word = btn.querySelector('[data-nh-bb-cap-add-text]');
      if (word) {
        // The count is the whole point of letting the pill add twice: without it
        // a second tap looks like nothing happened.
        word.textContent = on
          ? `${this.labels.tileAdded || 'In bundle'}${count > 1 ? ` · ${count}` : ''}`
          : (this.labels.tileAdd || 'Add');
      }
      const sr = btn.querySelector('[data-nh-bb-cap-add-label]');
      if (sr) {
        sr.textContent = on
          ? (this.labels.inBundleRemove || 'In your bundle — remove')
          : bcFill(this.labels.addPlate || '', { name: cap.name || key });
      }
    });
  }

  freeMoneyText(n) { return nhMoneyText(n); }

  /** The first plate the wall offers that is not already in the tray — what the
   *  nudge's "Add one" reaches for, so the button is never a dead end. */
  firstOfferedPlate() {
    const cats = (this.data && this.data.categories) || [];
    for (let ci = 0; ci < cats.length; ci += 1) {
      const list = (cats[ci] && cats[ci].plates) || [];
      for (let pi = 0; pi < list.length; pi += 1) {
        const p = list[pi];
        if (!p || p.variantId == null || p.available === false) continue;
        if (this.indexOfChoice(ci, pi) >= 0) continue;
        return { ci, pi };
      }
    }
    return null;
  }

  /** ROUND 23 — the price of one plate bought on its own. The 3+/5+ tiers are
   *  retired with ladder v2 (Trio 895 / Collector 1 195 / Refill 449); the two
   *  tier settings are still emitted but no longer price anything. */
  plateUnit() {
    const tiers = (this.labels && this.labels.plateTiers) || [195];
    return nhMoneyNum(String(tiers[0]));
  }

  /** The refill pack (3 plates, 449) if the section has one configured and it
   *  can be bought; null otherwise, in which case loose plates stay loose. */
  refillPack() {
    const r = this.data && this.data.refill;
    if (!r || r.variantId == null || r.available === false) return null;
    const plates = Number(r.plates) || 3;
    return { ...r, plates, priceNum: nhMoneyNum(String(r.price || '')) };
  }

  /** What a tray of ONE cap (`key`) and `n` plates costs if the first line is
   *  bundle `b` (null = the cap itself): the head's price plus every plate the
   *  head does not include, at the single-plate price. */
  headCost(b, n, cap) {
    const unit = this.plateUnit();
    if (!b) return nhMoneyNum(cap && cap.price) + Math.max(0, n - 1) * unit;
    const v = b.variants && b.variants[cap.key];
    return nhMoneyNum(v && v.price) + Math.max(0, n - Number(b.plates)) * unit;
  }

  /** The bundle a single cap and `n` plates should post, or null for the cap
   *  itself: whichever is CHEAPEST for the shopper. Measured ladder (v2):
   *  cap+1 695 · cap+2 890 · cap+3 895 Trio · cap+4 1 090 · cap+5 1 285 (Trio
   *  + 2 — the Collector's discount needs all six plates present) · cap+6
   *  1 195 · cap+7 1 390. A bundle whose colourway variant cannot be
   *  bought is never a candidate. */
  pickBundle(n, key, cap) {
    const capObj = cap || this.cap(key) || {};
    if (!(n >= 1)) return null;
    let best = null;
    let bestCost = this.headCost(null, n, capObj);
    (this.bundles || []).forEach((b) => {
      const v = b && b.variants ? b.variants[key] : null;
      if (!v || v.id == null || v.available === false) return;
      // MEASURED 2026-09-05: a BXGY whose "get" quantity is 6 does NOT fire on 5
      // plates — the cart charged Collector 1 195 + 5 × 195 = 2 170. A bundle is
      // a candidate only once the tray holds its whole plate count.
      if (n < Number(b.plates)) return;
      const c = this.headCost(b, n, { ...capObj, key });
      if (c < bestCost) { best = b; bestCost = c; }
    });
    return best;
  }

  /** Plates with NO cap: whole refill packs first, the rest loose. */
  platesOnlyPlan(n) {
    const pack = this.refillPack();
    const unit = this.plateUnit();
    if (!pack || n < pack.plates) return { packs: 0, loose: n, total: n * unit, pack: null };
    const packs = Math.floor(n / pack.plates);
    const loose = n - packs * pack.plates;
    return { packs, loose, total: packs * pack.priceNum + loose * unit, pack };
  }

  freeTotal() {
    const unit = this.plateUnit();
    const n = this.chosen.length;
    const caps = this.capsInTray();
    if (!caps.length) return this.platesOnlyPlan(n).total;
    const fc = this.freeCap();
    if (caps.length === 1) {
      // freeCap() has just chosen the head (bundle or cap); price the rest loose.
      const b = this.freeBundle;
      return this.headCost(b, n, { ...caps[0], key: fc ? fc.key : caps[0].key });
    }
    // Two or more caps: never a bundle (one BXGY per order); each cap frees one
    // plate of its own — measured 2 caps + 2 plates = 1 390.
    const paid = Math.max(0, n - caps.length);
    const capTotal = caps.reduce((sum, c) => sum + nhMoneyNum(c.price), 0);
    return capTotal + paid * unit;
  }

  /** Has the shopper actually settled on a cap, as syncBuy means it. */
  capReallyChosen() {
    return (this.capsChosen || []).length > 0;
  }

  /** Everything the tray posts, in order: the bundle variant if the tray IS a
   *  bundle, otherwise every cap, then every plate. One list, so the form does
   *  not have to know which kind of thing is in which row. */
  trayItems() {
    const out = [];
    const fc = this.freeCap();
    if (!this.capsInTray().length) {
      // ROUND 23 — plates only: whole refill packs post as ONE head line with a
      // quantity, and every plate rides along; the discount zeroes 3 per pack.
      const plan = this.platesOnlyPlan(this.chosen.length);
      if (plan.packs > 0) {
        this.partName = plan.pack.shortName || plan.pack.name || '';
        out.push({ id: plan.pack.variantId, part: this.partName, qty: plan.packs });
      }
    } else if (fc && this.freeBundle) {
      out.push({ id: fc.variantId, part: this.partName });
    } else {
      this.capsInTray().forEach((c) => {
        if (c.variantId != null && c.available !== false) out.push({ id: c.variantId, part: '' });
      });
    }
    this.chosen.forEach((ref) => {
      const p = this.plateAt(ref.ci, ref.pi);
      if (p && p.variantId != null && p.available !== false) {
        out.push({ id: p.variantId, part: this.partName, pre: !!p.preorder });
      }
    });
    return out;
  }

  /** How many lines the form can post: items[0] plus one row per rendered
   *  slot. Caps and plates share it, so it is the one number both adds obey. */
  trayCapacity() {
    return (this.slotCount || 12) + 1;
  }

  /** The bundle a tray of `caps` caps and `plates` plates would post, if any. */
  bundleNameAt(caps, plates) {
    if (caps === 0) {
      const plan = this.platesOnlyPlan(plates);
      return plan.packs > 0 && plan.loose === 0 ? (plan.pack.shortName || plan.pack.name || '') : '';
    }
    if (caps !== 1) return '';
    const key = (this.capsInTray()[0] || {}).key || this.boughtCapKey();
    const b = this.pickBundle(plates, key);
    return b ? (b.shortName || b.name || '') : '';
  }

  /** freeTotal() with `k` more plates in the tray, leaving nothing behind:
   *  freeCap() records the bundle a count implies as a side effect, so it is
   *  re-run at the real count afterwards. */
  freeTotalPlus(k) {
    for (let i = 0; i < k; i += 1) this.chosen.push({ ci: -1, pi: -1 });
    const v = this.freeTotal();
    for (let i = 0; i < k; i += 1) this.chosen.pop();
    this.freeCap();
    return v;
  }

  /**
   * ROUND 22.1 — THE NUDGE, LOOKING ONE AND TWO PLATES AHEAD, AND AT THE CAP.
   *
   * Sina's brief: "if they add a cap and a plate, say add two more and save
   * so much… when they have three, say if you had five you can get this."
   * The first cut only looked one plate ahead, so at the Trio it was silent —
   * exactly the moment he named — and at a cap and one plate it was silent
   * too, although two more plates there cost 150 each. And a tray with two
   * caps and one plate, where the NEXT PLATE IS FREE, showed nothing at all,
   * because a delta of zero failed a "> 0" test written for savings.
   *
   * Four shapes, tried in order, each measured against the live ladder:
   *   cap   — no cap in the tray: what a cap adds, net of the plate it frees
   *           (500 kr at every count but five, where it is 470).
   *   free  — the next plate costs nothing (a second cap's own free plate).
   *   one   — the next plate costs at most the 3+ tier price: 105 at cap+2,
   *           55 at cap+4, 135 at two caps and four plates, 165 at 3→4.
   *   two   — two more plates average at most that tier: 150 each from cap+1
   *           to the Trio, 125 each from the Trio to the Collector.
   * The threshold is the 3+ tier, not "any saving": from the Collector two more
   * plates average 190, which is a saving of five kronor and not a reason to
   * speak. Every number here comes from freeTotal(), never from copy.
   */
  freeNudge() {
    // ROUND 23 — no tiers: anything cheaper than a plate on its own is news
    // (100 each towards the Trio, 5 kr for the third, 127 each towards a pack).
    const base = this.plateUnit();
    const good = base;
    const n = this.chosen.length;
    const caps = this.capsInTray().length;
    const room = this.trayCapacity() - this.trayItems().length;
    const here = this.freeTotal();

    if (!caps) {
      if (n < 1 || room < 1) return null;
      const key = this.boughtCapKey() || ((this.data && this.data.caps && this.data.caps[0]) || {}).key;
      if (!key || !this.cap(key)) return null;
      if (!Array.isArray(this.capsChosen)) this.capsChosen = [];
      this.capsChosen.push(key);
      const withCap = this.freeTotal();
      this.capsChosen.pop();
      this.freeCap();
      const delta = withCap - here;
      if (!(delta > 0)) return null;
      return { kind: 'cap', delta, next: withCap, full: base, name: this.bundleNameAt(1, n) };
    }

    if (room >= 1) {
      const p1 = this.freeTotalPlus(1);
      const d1 = p1 - here;
      if (d1 === 0) return { kind: 'free', delta: 0, next: p1, full: base, name: this.bundleNameAt(caps, n + 1) };
      // ROUND 23 — one more plate and the total goes DOWN (cap+5 = Trio + 2 =
      // 1 285; cap+6 = Collector 1 195). Say so, with the amount.
      if (d1 < 0) return { kind: 'less', delta: -d1, next: p1, full: base, name: this.bundleNameAt(caps, n + 1) };
      if (d1 > 0 && d1 < good) {
        return { kind: 'one', delta: d1, next: p1, full: base, name: this.bundleNameAt(caps, n + 1) };
      }
    }
    if (room >= 2) {
      const p2 = this.freeTotalPlus(2);
      const each = (p2 - here) / 2;
      // Strictly better than the 3+ tier: two more at exactly the tier price is
      // the ordinary volume offer, not news.
      if (each > 0 && each < good) {
        return { kind: 'two', delta: p2 - here, each, next: p2, full: base, name: this.bundleNameAt(caps, n + 2) };
      }
    }
    return null;
  }

  capChosen() {
    const b = this.bundle;
    const key = this.boughtCapKey();
    if (!b || !key) return null;
    const cap = this.cap(key) || {};
    const v = (b.variants || {})[key];
    if (!v) {
      // A colourway this bundle has no variant for: syncBuy sees a picked cap
      // that cannot be bought and disables with "Sold out" — its existing
      // branch, no new code.
      return { key, name: cap.name || key, variantId: null, price: '', available: false };
    }
    return {
      key,
      name: cap.name || key,
      variantId: v.id,
      price: v.price || '',
      available: v.available !== false,
    };
  }

  /** The core marks the tile a chosen plate came from; on this surface the
   *  wall tile is the <label>, which only carries data-cat/data-index while
   *  bundle mode is rendered. Empty in plain mode, so markChosen is a no-op. */
  collectBundleNodes() {
    const q = (sel) => this.querySelector(sel);
    const root = this.bcRoot();
    const rq = (sel) => (root ? root.querySelector(sel) : null);
    Object.assign(this.els, {
      media: this.querySelector('.nh-ps__media'),
      deckHost: rq('[data-nh-bb-deckhost]'),
      dockHost: rq('[data-nh-bb-dockhost]'),
      deck: rq('[data-nh-bb-deck]'),
      trayBlock: rq('[data-nh-bb-trayblock]'),
      trayTitle: rq('.nh-bb__traytitle'),
      tray: rq('[data-nh-bb-tray]'),
      count: rq('[data-nh-bb-count]'),
      toast: rq('[data-nh-bb-toast]'),
      toastText: rq('[data-nh-bb-toast-text]'),
      prenote: rq('[data-nh-bb-prenote]'),
      group: rq('[data-nh-bb-group]'),
      form: rq('[data-nh-bb-form]'),
      variant: rq('[data-nh-bb-variant]'),
      button: rq('[data-nh-bb-button]'),
      label: rq('[data-nh-bb-label]'),
      price: rq('[data-nh-bb-price]'),
      name: rq('[data-nh-bb-name]'),
      status: rq('[data-nh-bb-status]'),
      error: rq('[data-nh-bb-error]'),
      wrapper: rq('[data-nh-bb-wrapper]'),
      buy: rq('.nh-bb__buy'),
      bar: rq('[data-nh-bb-bar]'),
      endSentinel: rq('[data-nh-bb-endsentinel]'),
      dock: rq('[data-nh-bb-dock]'),
      scrim: rq('[data-nh-bb-scrim]'),
      // THESE THREE CARRY THE CORE'S NAMES, not this section's. `els.sheet`,
      // `els.sheetGrip` and `els.sheetBody` are what the shared dock code
      // reads; this section's OWN bottom sheet was renamed to psSheet / psGrip
      // / psBody in round 18 to leave them free. Getting this wrong is silent:
      // placeDock() returns early on a missing `sheet`, the dock never opens,
      // and the buy bar sits in the page flow 400px wide (measured).
      sheet: rq('[data-nh-bb-sheet]'),
      sheetGrip: rq('[data-nh-bb-sheet-grip]'),
      sheetBody: rq('[data-nh-bb-sheet-body]'),
      sheetHost: rq('[data-nh-bb-sheet-host]'),
      sheetAdd: rq('[data-nh-bb-sheet-add]'),
      variantQty: rq('[data-nh-bb-variant-qty]'),
      sheetAddLabel: rq('[data-nh-bb-sheet-add-label]'),
      sheetAddName: rq('[data-nh-bb-sheet-add-name]'),
      sheetFoot: rq('[data-nh-bb-sheetfoot]'),

      // ROUND 19 — THE SHARED PANEL. The cap control, the caption row and the
      // gallery strip are the bundle page's markup, from
      // snippets/nordenheim-bundle-panel.liquid, driven by the core. Captured
      // here with everything else, once, for the reason the block above gives:
      // on a phone the whole picture panel travels into the dock, and a
      // `this.querySelector` after that move finds nothing.
      capCombo: rq('[data-nh-bb-capcombo]'),
      capAffix: rq('[data-nh-bb-cap-affix]'),
      capBtnThumb: rq('[data-nh-bb-capbtn-thumb]'),
      capModal: rq('[data-nh-bb-capmodal]'),
      capModalBody: rq('[data-nh-bb-capmodal-body]'),
      capline: rq('.nh-bb__capline'),
      strip: rq('[data-nh-bb-strip]'),
      info: rq('[data-nh-bb-info]'),
      infoName: rq('[data-nh-bb-info-name]'),
      pop: rq('[data-nh-bb-pop]'),
      popTitle: rq('[data-nh-bb-pop-title]'),
      popCollab: rq('[data-nh-bb-pop-collab]'),
      popBody: rq('[data-nh-bb-pop-body]'),
      popLink: rq('[data-nh-bb-pop-link]'),
      atcRow: rq('.nh-ps__atc-row'),
    });
    // The cap LISTBOX and its button, held directly: the phone's chooser MOVES
    // the <ul> into a <dialog> and back, so `capCombo.querySelector` answers
    // differently depending on where it is at the time. Same capture the
    // builder has made since round 11.
    this.els.capList = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-combo-list]')
      : null;
    this.els.capBtn = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-combo-btn]')
      : null;
    this.els.capName = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-cap-name]')
      : null;
    this.els.capAffix = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-cap-affix]')
      : this.els.capAffix;
    this.combos = Array.from(root.querySelectorAll('[data-nh-bb-combo]'));

    // ROUND 19 — THE CAPTION SWAP. `els.capNote` is what the core's
    // paintCapNote() writes, and from here it is the SHARED band. This
    // section's own "Shown on {cap}" line is kept under psCapNote (hidden by
    // the card layout, and still the price's only home for a screen reader);
    // psNote() is what every writer in this file now reaches for, so the
    // default path — where this line never runs — is untouched.
    const shared = rq('[data-nh-bb-cap-note]');
    if (shared) {
      this.els.psCapNote = this.els.capNote;
      this.els.capNote = shared;
    }
    // What renderStrip / markStrip / paintCapNote read. -1 is "the real
    // combination", which is what a cold load shows.
    if (typeof this.shotIndex !== 'number') this.shotIndex = -1;
    // ROUND 15's lesson, one file over: a move OUT of this element is not a
    // move inside it. Everything the core touches after the bar and the media
    // have gone into the dock is captured HERE, once, and never re-queried —
    // the tray's checkboxes and the form's per-slot inputs above all, because
    // a `this.querySelector` that finds nothing at submit time leaves every
    // plate line disabled and posts a bundle as ONE line.
    this.allSlots = Array.from(root.querySelectorAll('[data-nh-bb-slot]'));
    this.allConfirms = Array.from(root.querySelectorAll('[data-nh-bb-confirm]'));
    this.allItemInputs = [];
    for (let i = 1; i <= this.slotCount; i += 1) {
      this.allItemInputs[i] = {
        id: rq(`[data-nh-bb-item="${i}"]`),
        qty: rq(`[data-nh-bb-item-qty="${i}"]`),
        grp: rq(`[data-nh-bb-item-group="${i}"]`),
        part: rq(`[data-nh-bb-item-part="${i}"]`),
        pre: rq(`[data-nh-bb-item-pre="${i}"]`),
      };
    }
    this.slots = [];
    this.confirms = [];
    this.itemInputs = [];
    this.previewButtons = Array.from(this.querySelectorAll('.nh-ps__thumb[data-cat]'));
    this.catPanels = this.panels || [];
    // The core's updateRungLinks walks these; on this surface the state travels
    // in the address bar instead (see updateRungLinks below).
    this.rungs = [];
    this.mixLinks = [];
    if (this.els.bar) {
      this.barHome = this.els.bar.parentNode;
      this.barNext = this.els.bar.nextSibling;
    }
    // The dock's stage swipe steps PLATES; this section's own swipe on the
    // same element steps COLOURWAYS and is bound already. Two handlers on one
    // element both fire, so the core is told to leave it alone.
    this.bcStageSwipe = false;
  }

  /* ------------------------------------------------------- the mode switch */

  /**
   * ONE ENTRY POINT for all eight transitions between the four picker rows.
   *
   * WHAT HAPPENS TO ALREADY-CHOSEN PLATES, decided:
   *   Trio → Collector      every plate kept, two slots appear, the count and
   *                         the button just say what is left.
   *   Collector → Trio      the FIRST THREE IN CHOICE ORDER are kept and the
   *                         surplus is dropped, announced once through the
   *                         toast. No Undo on this path: an Undo that restores
   *                         plates the smaller bundle cannot hold is a control
   *                         that cannot honour itself, and addPlate already
   *                         refuses past `max`.
   *   bundle → Starter      `chosen` is REMEMBERED, not cleared — the tray and
   *   or Plates only        the form leave the layout, the array survives, and
   *                         coming back restores it. Someone flicking through
   *                         four rows to compare must not lose four taps.
   *   Trio → Starter → Collector   the remembered array is re-applied against
   *                         the new max, i.e. the truncation rule above.
   * `chosen` is cleared on a successful add and on nothing else.
   * The cap and the previewed plate are carried across every switch untouched.
   */
  setBundle(handleOrNull, { announce = true } = {}) {
    if (!this.bundles) return;
    const next = handleOrNull ? this.bundles.find((b) => b.handle === handleOrNull) : null;
    const wasOn = this.bundleOn;
    this.bundle = next || null;
    this.bundleOn = !!next;
    const root = this.bcRoot();

    if (next) {
      // From here on this section owns `bundle`/`plates` in the address bar,
      // which is what lets syncUrl clean them up again on the way out.
      this.bundleUrlOwned = true;
      // Parked on the way out of bundle mode (below); put back on the way in,
      // and then trimmed to this bundle's max by the two lines after it.
      if (!this.chosen.length && this.chosenParked && this.chosenParked.length) {
        this.chosen = this.chosenParked.slice();
        this.chosenParked = null;
      }
      const before = this.chosen.length;
      this.max = Math.max(1, Math.min(this.slotCount, Number(next.plates) || 1));
      if (this.chosen.length > this.max) this.chosen.length = this.max;
      const dropped = before - this.chosen.length;
      // The tray, the checkboxes and the form rows this bundle actually uses.
      // The rows past `max` keep the `disabled` the server rendered, so a
      // shrink can never post a variant the smaller bundle did not price.
      this.slots = this.allSlots.slice(0, this.max);
      this.confirms = this.allConfirms.slice(0, this.max);
      this.itemInputs = this.allItemInputs.slice(0, this.max + 1);
      this.allSlots.forEach((s, i) => { s.hidden = i >= this.max; });
      for (let i = this.max + 1; i <= this.slotCount; i += 1) {
        const it = this.allItemInputs[i] || {};
        ['id', 'qty', 'grp', 'part', 'pre'].forEach((k) => {
          if (it[k]) { it[k].disabled = true; if (k === 'id') it[k].value = ''; }
        });
      }
      // `{name}` in the enabled button label, and the value of every plate
      // line's `Part of` — both of which change with the bundle and therefore
      // cannot be the server-rendered ones.
      this.labels.name = next.shortName || next.name || '';
      this.partName = next.shortName || next.name || '';
      this.paintTrayTitle();
      root.classList.remove('nh-ps--mirror');
      if (this.els.deckHost) this.els.deckHost.hidden = false;
      if (this.els.dockHost) this.els.dockHost.hidden = false;
      root.classList.add('nh-ps--bundle');
      if (dropped > 0 && announce) {
        // ROUND 18.1 — A SINGULAR FORM, the same treatment round 14 gave
        // hintOne/hintMany and hintBothOne. Collector -> Trio with four plates
        // chosen drops exactly one, which is the commonest case of all, and it
        // read "1 plates removed — the Trio holds 3".
        const msg = (dropped === 1 && this.labels.bundleTrimmedOne)
          ? this.labels.bundleTrimmedOne
          : this.labels.bundleTrimmed;
        if (msg) {
          this.say(bcFill(msg, { count: dropped, name: this.labels.name, total: this.max }), true);
          this.sayStatus(bcFill(msg, { count: dropped, name: this.labels.name, total: this.max }));
        }
      }
    } else {
      root.classList.remove('nh-ps--bundle');
      // Park and release BEFORE the dock leaves the layout, so nothing is left
      // inert and the picture panel comes home first.
      if (wasOn) this.setSheet(false, { instant: true });
      this.slots = [];
      this.confirms = [];
      this.itemInputs = [];
      // ROUND 18.1 — DEAD STATE IS STILL STATE. Emptying `itemInputs` stops
      // syncBuy WRITING to the rows; it does not empty the rows it already
      // wrote. Measured after Trio -> Starter: items[1..3] still held live
      // plate variant ids, still enabled, beside a blank items[0] — three
      // plate lines and a `Part of: Trio` one submit away from a cart with no
      // bundle in it. Nothing reaches that form today (the plain buttons are a
      // separate <form> and this one is hidden and disabled), which is exactly
      // why it would have gone unnoticed. Blank and disable every plate row,
      // and put `max` back where a plain section has it.
      this.max = 1;
      this.partName = '';
      this.allSlots.forEach((slot) => { slot.hidden = true; });
      for (let i = 1; i <= this.slotCount; i += 1) {
        const it = this.allItemInputs[i] || {};
        ['id', 'qty', 'grp', 'part', 'pre'].forEach((k) => {
          if (it[k]) { it[k].disabled = true; if (k === 'id') it[k].value = ''; }
        });
      }
      // ROUND 19 — THE DECK STAYS. Starter and Plates only are modes of the
      // same panel, not the absence of one: the deck holds a single tray slot
      // showing the plate on the stage, the cap control (Starter only), and
      // that mode's own button — "Add cap + plate" or "Add plate to cart",
      // which are the two <form>s this section has always had, captured into
      // the deck by the Liquid rather than rendered a second time.
      //
      // THE MIRROR SLOT IS NOT A CHOICE. `chosen` is left empty, so slot 0
      // falls into renderTray's CANDIDATE branch and draws the previewed
      // plate's own artwork and name; `.nh-ps--mirror` hides its checkbox, so
      // there is nothing to tick and nothing to remember. That matters on the
      // way back: a plate "chosen" in Starter must not walk into a Trio the
      // shopper then picks, and round 18's rule — the array is REMEMBERED
      // across a switch — would have carried it there.
      root.classList.add('nh-ps--mirror');
      // ROUND 19 — THE MIRROR TRAY'S OWN HEADING. `paintTrayTitle()` fills
      // "Your plates — choose any {plates}" from `max`, which reads "choose any
      // 1" here — an instruction for a tile that is not a choice. One slot
      // showing the plate on the stage gets a heading that says so, and it is a
      // setting with its own Swedish like every other string on this panel.
      // ROUND 18's rule survives: the chosen array is REMEMBERED across a
      // switch, so someone flicking through four rows to compare does not lose
      // four taps. It is PARKED rather than kept live, because slot 0 has to
      // fall into renderTray's candidate branch to mirror the stage — a filled
      // slot would show the first plate they chose in a Trio they have left.
      // Re-entering a bundle puts it straight back, against the new max.
      // ROUND 22 — the plain modes KEEP their choice. Round 19 emptied `chosen`
      // here because slot 0 had to fall into renderTray's candidate branch to
      // mirror the stage; the slot is a real one now (see setPlainRow), so
      // emptying it is what made Starter read "0 of 1 chosen" no matter what was
      // clicked, and what wiped a Starter's plate whenever anything called
      // setBundle(null) again — picking a cap among them.
      if (!this.trayGrows && this.chosen.length > 1) {
        this.chosenParked = this.chosen.slice();
        this.chosen.length = 1;
      }
      this.slots = this.allSlots.slice(0, 1);
      this.confirms = this.allConfirms.slice(0, 1);
      this.itemInputs = this.allItemInputs.slice(0, 2);
      this.allSlots.forEach((slot, i) => { slot.hidden = i >= 1; });
      // `itemInputs` stays EMPTY: syncBuy must write nothing into the bundle
      // form in these two modes, and the rows were blanked and disabled above.
      if (this.els.deckHost) this.els.deckHost.hidden = false;
    }

    this.syncTrayWidth();
    this.moveBuy();
    this.renderTray();
    this.syncBuy();
    this.markChosen();
    if (next) this.syncSheetTitle();
    // ROUND 19 — THE DOCK HOST STAYS UNHIDDEN in every mode. The dock is the
    // phone's shell for the deck, and the deck is now on in all four modes;
    // which of the two shells is actually shown is placeDock()'s decision
    // (`dock.hidden`), not this one's. Hiding the host here as well is how the
    // buy bar came to have two owners.
    if (this.els.dockHost) this.els.dockHost.hidden = false;
    // ROUND 20 — THE CAP BEING BOUGHT, not the one on the stage. Caught in
    // verification, not in reading: switching to "Plates only" while looking at
    // Aska repainted the control to "Your cap – Aska Cotton Trucker" while the
    // purchase was still Björk. The control is `display: none` in that one mode,
    // so it was invisible there and would have been wrong the moment the shopper
    // switched back. Third call site of the same mistake this round; every one
    // of them now reads psBoughtCap().
    this.paintCapButton(this.psBoughtCap());
    this.renderStrip();
    this.paintCapNote();
    this.paintCapRow();
    if (this.picker && this.picker.setCurrent) {
      this.picker.setCurrent(next ? next.handle : this.plainRow);
    }
    this.syncUrl();
    this.rescueFocus();
  }

  /**
   * ROUND 19.1 — FOCUS SURVIVES A MODE CHANGE ON A PHONE (WCAG 2.4.3).
   *
   * The picker restores focus to its own button after a row is chosen, which is
   * right everywhere except one place: round 19 moved the picker INSIDE the
   * card, and on a phone entering Trio or Collector the card is re-parented
   * into the dock's `.nh-bb__sheet-body`, which is `inert` and
   * `visibility: hidden` while the sheet is parked. Calling focus() on a button
   * inside an inert subtree does nothing, so focus fell to <body> and a
   * keyboard or switch user lost their place in the page. Measured at 375x812:
   * Starter -> Trio left `document.activeElement === document.body`; desktop and
   * the reverse direction (Trio -> Starter) were both already correct.
   *
   * Only fires when focus was ACTUALLY lost — if the picker's own restore
   * landed, or the shopper was never in the picker (a deep link, a bundle card,
   * the ‹ › keys), there is nothing to rescue and this must not steal focus.
   * The rAF is what puts it after the picker's own restore in the same frame.
   */
  rescueFocus() {
    const btn = this.picker && this.picker.querySelector('[data-nh-bp-btn]');
    if (!btn) return;
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) return;
      // `offsetParent` is null for a display:none ancestor; `closest('[inert]')`
      // catches the parked sheet, which is visibility-hidden rather than
      // display-none and therefore still has one.
      const reachable = btn.offsetParent !== null && !btn.closest('[inert]');
      if (reachable) { btn.focus(); return; }
      // The dock's grip is the one visible control the mode change just put on
      // screen, and it is what opens the sheet the picker now lives in.
      const grip = this.qs('[data-nh-bb-sheet-grip]')
        || (this.els.dock && this.els.dock.querySelector('[data-nh-bb-sheet-grip]'));
      if (grip && grip.offsetParent !== null) grip.focus();
    });
  }

  /**
   * ROUND 18.1 — DOES THIS HREF NAME A BUNDLE THIS SECTION CAN BUILD?
   *
   * Two shapes are accepted, because both exist on the page: the bundle
   * product's own URL (what the "Want more plates?" strip and the bundle cards
   * carry) and this surface's own `?bundle=<handle>` deep link. Compared on the
   * PATHNAME, so a locale root, a query string and a fragment are all
   * irrelevant, and a link to another page's product is not matched.
   */
  bundleForHref(href) {
    if (!href || !this.bundles || !this.bundles.length) return null;
    let u;
    try { u = new URL(href, window.location.href); } catch (e) { return null; }
    if (u.origin !== window.location.origin) return null;
    const wanted = (u.searchParams.get('bundle') || '').trim();
    if (wanted && u.pathname === window.location.pathname) {
      return this.bundles.find((b) => b.handle === wanted) || null;
    }
    return this.bundles.find((b) => {
      if (!b.url) return false;
      try { return new URL(b.url, window.location.href).pathname === u.pathname; }
      catch (e) { return false; }
    }) || null;
  }

  /**
   * ROUND 18.1 — THE TICK IS DRAWN AT EVERY WIDTH HERE, so the accessible
   * equivalent belongs at every width too. The bundle PDP paints its tick only
   * inside the phone Tray Sheet and its core default says so; this section's
   * rule is `.nh-ps--bundle .nh-ps__thumb.is-chosen::after`, unscoped by
   * design — the wall is 106 tiles long and the desktop tray is a strip inside
   * the picture panel, so the second reading earns its place. Declared in
   * `owns` at the foot of this file.
   */
  chosenMarkVisible() {
    return true;
  }

  /**
   * The cap variants that already carry a BXGY offer of their own ("Art Cap":
   * buy a cap, get a plate). One of those in the cart plus a bundle is the same
   * over-charge the core's cartClash() guards — Shopify applies one BXGY per
   * order — and this section renders every cap it sells, so the ids are already
   * to hand and cost nothing to hand over.
   */
  /** The bundle this tray is about to post — the one freeCap() picked when the
   *  count matched — so the clash guard compares against the right handle. The
   *  core's own version reads `this.bundle`, which a free tray never sets. */
  cartBundleHandle() {
    if (this.freeBundle && this.freeBundle.handle) return this.freeBundle.handle;
    return (this.bundle && this.bundle.handle) || (this.settings && this.settings.handle) || '';
  }

  cartClashVariantIds() {
    return ((this.data && this.data.caps) || []).map((c) => c.variantId).filter((v) => v != null);
  }

  /**
   * The phone Tray Sheet is a `role="dialog"`, and a dialog needs a name. The
   * shared dock names it through `nh-bb-caption-<section id>`, which on this
   * surface is the visually-hidden <p> the dock's `for: 'host'` branch renders;
   * nothing filled it, so the dialog opened unnamed. Written on every plate and
   * every colourway change, which is every route that changes what the sheet
   * is showing.
   */
  syncSheetTitle() {
    const el = this.qs('[data-nh-bb-sheet-title]');
    if (!el) return;
    const plate = this.currentPlate && this.currentPlate();
    const cap = this.cap(this.capKey) || {};
    const parts = [plate && plate.name, cap.name].filter(Boolean);
    if (parts.length) el.textContent = parts.join(' \u00d7 ');
  }

  /** "Plates only" and "Starter" are the two PLAIN sub-states: same wall, same
   *  cap swatches, same URL sync — one class decides whether the cap button is
   *  offered. No JS state at all beyond which row is current. */
  setPlainRow(row) {
    // ROUND 22 — THREE PLAIN ROWS: 'free' (cap + any number of plates, the
    // DEFAULT), 'starter' (cap + exactly one) and 'mix' (plates alone).
    this.plainRow = (row === 'mix' || row === 'starter') ? row : 'free';
    const plates = this.plainRow === 'mix';
    this.freeMode = this.plainRow === 'free';
    const root = this.bcRoot();
    root.classList.toggle('nh-ps--plates-only', plates);
    root.classList.toggle('nh-ps--free', this.freeMode);
    // ROUND 22, PASS B — "Plates only" gets a real tray (Sina, 2026-09-04:
    // "for the plates only it would enable the user to add as many as they want
    // … right now only one added plate is possible").
    //
    // Starter keeps the mirror: its one slot SHOWS the plate on the stage and is
    // not a choice, which is round 19's distinction and still right for a mode
    // that buys exactly one plate. Plates only is the opposite — the slots are
    // the choices — so the mirror comes off and the tray grows with them.
    const grows = plates || this.freeMode;
    if (this.trayGrows === grows && this.platesMode === plates) return;
    this.trayGrows = grows;
    this.platesMode = plates;
    root.classList.remove('nh-ps--mirror');
    if (grows) {
      // Whatever was parked on the way out of a bundle comes back, exactly as
      // re-entering a bundle restores it.
      if (!this.chosen.length && this.chosenParked && this.chosenParked.length) {
        this.chosen = this.chosenParked.slice();
        this.chosenParked = null;
      }
      this.syncPlatesMax();
      this.paintTrayTitle();
    } else {
      // ROUND 22 — STARTER IS A ONE-SLOT TRAY, NOT A MIRROR.
      //
      // Sina, 2026-09-04: "for the starter bundle it is not possible to add a
      // plate, nothing happens." Round 19 made this slot MIRROR the stage — it
      // showed the previewed plate and held no choice — so the counter sat at
      // "0 of 1 chosen" however many plates were clicked, and the wall's new Add
      // had nothing to fill. It is a real slot now: one plate, chosen the same
      // way Trio's three are, with items[0] still the cap (that is what keeps
      // this different from "Plates only", which disables items[0] entirely).
      this.max = 1;
      if (!this.chosen.length && this.chosenParked && this.chosenParked.length) {
        this.chosen = this.chosenParked.slice();
        this.chosenParked = null;
      }
      if (this.chosen.length > this.max) this.chosen.length = this.max;
      this.slots = this.allSlots.slice(0, 1);
      this.confirms = this.allConfirms.slice(0, 1);
      this.itemInputs = this.allItemInputs.slice(0, 2);
      this.allSlots.forEach((slot, i) => { slot.hidden = i >= 1; });
      // ROUND 22 — BUT NOT IN "Plates only". setBundle(null) is shared by both
      // plain rows, so the one-slot shape above was being forced onto the plates
      // tray as well; setPlainRow() could not undo it, because it early-returns
      // when the mode has not CHANGED. Measured: a plates-only tray stuck at
      // "1 of 1 chosen" under a heading reading "choose any 5".
      if (this.trayGrows) this.syncPlatesMax();
    }
    this.syncTrayWidth();
    this.moveBuy();
    this.afterChange();
  }

  /**
   * ROUND 19 — WHAT A ROW IN THE SHARED "Your cap" DROPDOWN DOES HERE.
   *
   * The core's chooseOption() calls this. On the bundle PDP `selectCap()` IS
   * the colourway state — it sets `capKey` and paints. On this surface the
   * state is, and stays, the native radio group in `.nh-ps__caps`: eight
   * <input type="radio"> whose `change` runs userPick, applyCap, the ?cap=
   * sync and haltAutoplay, and whose `checked` is what markRail(-1) restores
   * and what a screen reader announces.
   *
   * So this does not reimplement any of that. It CLICKS THE REAL RADIO — the
   * same trick restoreFromUrl() has used since round 18 — and every one of
   * those paths runs exactly as it does for a swatch click. There is one
   * source of truth for the colourway on this page and it is not the dropdown.
   *
   * The radios stay in the DOM while the dropdown is on; the fieldset around
   * them is `hidden`, so they leave the tab order with it and the shopper is
   * never given two controls for one decision.
   */
  selectCap(key) {
    const cap = this.cap(key);
    if (!cap) return;
    // ROUND 22.2 (Sina: "the same mini-image should show when the user clicks
    // another plate"). A colourway chip in the strip lands here; record it
    // like the cap radios do, so the next plate opens on this colourway's
    // mockup instead of the worn slot picked earlier.
    this.userPick = { type: 'cap' };
    // ROUND 20 — THIS IS THE ONE PLACE THE PURCHASED COLOURWAY MOVES.
    //
    // Rule 2 of the round, and the whole reason `boughtCap` exists as its own
    // field: the cap row looks, the gallery chips look, the arrows look, ?cap=
    // looks — this buys. Set BEFORE the radio click below, because that click
    // runs applyCap synchronously and applyCap now reads `boughtCap` to point
    // the "Add cap + plate" button and syncBuy() to pick the bundle variant.
    this.boughtCap = cap.key;
    const input = this.qs(`[data-nh-cap-input][value="${cssAttr(cap.key)}"]`);
    if (input && !input.checked) {
      // The chooser sets BOTH — round 17 item 5's rule, kept: a strip chip
      // changes the view only, the cap chooser changes the view and the order.
      // `change` does not fire for a programmatic `checked`; dispatching it is
      // what makes this the ordinary path rather than a parallel one.
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // Already the colourway on the stage — nothing to change there, but the
      // purchase may just have moved onto it, so the buttons still have to be
      // repainted (a cold load reaches here too).
      this.applyCap(cap.key, { animate: false });
    }
    this.paintCapButton(cap);
    this.paintCapRow();
    this.syncCapAddToCart(cap);
    if (this.bundleOn) {
      this.syncBuy();
      this.syncSheetTitle();
    }
  }

  /**
   * ROUND 19 — the tray's heading in Starter and Plates only.
   *
   * The shared paintTrayTitle() caches the TEMPLATE in a data attribute the
   * first time it runs, so writing a different sentence over the element here
   * cannot destroy it: switching into a bundle calls paintTrayTitle() again and
   * the template comes back out of the attribute. Nothing is lost and nothing
   * has to be re-read from the server.
   */
  /**
   * ROUND 19.1 — IS THE BUYING DECK ON SCREEN AT ALL?
   *
   * `bundleOn` answers "is a Trio or a Collector selected", which is NOT the
   * same question since round 19 gave Starter and "Plates only" the same deck
   * with a single mirror slot in it. Every repaint that was guarded on
   * `bundleOn` therefore stopped at the two plain modes, and the mirror slot —
   * which is drawn by renderTray's candidate branch from the PREVIEWED plate —
   * froze on whichever plate happened to be on the stage when the mode was
   * entered. Measured: the tray read "Bästkusten" through three different
   * plate selections while the caption and the button had moved on.
   */
  cardOn() {
    // `Array.isArray(this.chosen)` is a READINESS test, not defensiveness.
    // build() calls selectPlate() to seed the stage BEFORE buildBundle() has
    // created `chosen`, and the class is on the section from the server — so
    // asking the class alone sent the very first paint into the core's
    // candidate(), which reads `this.chosen.length` and threw
    // "Cannot read properties of undefined". That aborted build() half-way and
    // the caption and gallery bands were never drawn at all. The gate the old
    // `bundleOn` test gave for free has to be stated now that it is gone.
    return Array.isArray(this.chosen) && this.bcRoot().classList.contains('nh-ps--card');
  }

  /**
   * ROUND 19.1 — THE FIVE-SLOT TRAY WRAPS, AND THE CARD IS HEIGHT-BOUNDED.
   *
   * `--nh-ps-card-chrome` (nordenheim-plate-snap.css) is how much of the card
   * is NOT the picture, and Collector's tray is 55px taller than Trio's because
   * its five slots take two rows. CSS cannot count slots, so the one bit the
   * stylesheet cannot work out for itself is handed over as a class. Nothing
   * else reads it.
   */
  syncTrayWidth() {
    // Plates only has no fixed offer, so its row is a WRAPPING grid of five
    // rather than one column per slot: `max` there is "chosen + 1" and would
    // otherwise re-lay the tray on every tap.
    // ROUND 22 (Sina: "öka rader istället för att minska storlek efter 5 per
    // rad"). FIVE COLUMNS IS THE CEILING IN EVERY MODE. The column count also
    // sets the row's max-width, so letting it grow past five made the tracks
    // divide the same width into ever-narrower tiles — at seven items the
    // artwork was unreadable. Clamped, the sixth item wraps to a second row at
    // full size, which is the trade he asked for. Below five nothing changes:
    // a Trio still lays its three out across the row.
    // ROUND 24.1 (Sina, 2026-09-07: "why 2 rows for the plates when there is
    // enough space on one row?"). COUNT THE CAP. `max` is a PLATE count, but
    // since round 21 the cap is a tray item sharing this grid — so a Trio asked
    // for 3 columns and then put four chips in them, and the cap wrapped onto a
    // row of its own with 190px of the deck standing empty beside it. The column
    // count also sets the row's max-width, so the empty space was locked in: at
    // 3 columns the row can only ever be 254px.
    //
    // Capacity, not occupancy: the tray is laid out for what it will hold when
    // it is full, so filling it does not re-flow the row under the shopper's
    // finger. Two caps make it seven, which the clamp below turns back into a
    // wrap at full tile size — round 22's trade, unchanged.
    const caps = this.capsInTray ? this.capsInTray().length : 0;
    // ROUND 24.2 — the empty cap box is a CELL, so it has to be counted or the
    // fix above just moves the wrap: a Trio with no cap would ask for three
    // columns and then hold a placeholder plus three slots. `trayGrows` is the
    // free tray, where 22.1 hides every empty box and there is nothing to count.
    const capCells = caps || (this.trayGrows ? 0 : 1);
    const n = Math.min(5, Math.max(1, (this.max || 1) + capCells));
    this.bcRoot().classList.toggle('nh-ps--wide-tray', n > 3);
    // ROUND 22 — the shared tray's own variable. `.nh-bb__tray` is
    // `repeat(var(--nh-bb-tray-cols, 3), minmax(0, 80px))` with a matching
    // max-width, and nothing here ever wrote it: a Collector's five slots kept
    // the 3-column default and wrapped onto a second row while the bundle page
    // laid the same five out in one. Sina, 2026-09-04: "the bundle page has 5
    // plate holders on the same row while image 1 has them over 2 rows."
    if (this.els.trayBlock) {
      this.els.trayBlock.style.setProperty('--nh-bb-tray-cols', String(n));
    }
    const block = this.els.trayBlock || this;
    const tray = block.querySelector ? block.querySelector('.nh-bb__tray') : null;
    if (tray) tray.style.setProperty('--nh-bb-tray-cols', String(n));
  }

  paintMirrorTitle() {
    const el = this.els.trayTitle;
    if (!el) return;
    // Seed the cache before overwriting the element, or a section that lands in
    // Starter (which is every cold load) would lose the bundle template
    // entirely and the first switch into a Trio would show this line instead.
    if (!el.dataset.nhBbTrayTitle) el.dataset.nhBbTrayTitle = el.textContent || '';
    const own = (this.settings.labels || {}).mirrorTray;
    if (own) el.textContent = own;
  }

  /* ============================================== ROUND 20 — THE CAP ROW

     The eight Art Caps as product cards at the top of the plate wall, and the
     cap-only view a card opens. Six rules were approved for it; three of them
     are enforced here.

       1. THE ROW LOOKS, IT DOES NOT BUY. A card opens that cap ALONE — its own
          photographs on the stage and in the gallery band, its name as the
          caption, and nothing else: no plate composite, no other colourway.
       2. THE PURCHASE MOVES ONLY IN THE "Your cap" CONTROL. See selectCap()
          and boughtCapKey(); this file's only writer of `boughtCap` is that
          one method. A card marks the bought cap so the difference is visible.
       3. THE PLATE FLOW IS UNCHANGED. A plate click calls exitSoloCap() and
          the panel is back to what it was, byte for byte.

     None of it exists off the card: `bundle_inplace` gates the markup, so the
     row is not rendered, `soloCapKey` is never set, and `boughtCap` is never
     seeded — which is what makes the live default path unchanged rather than
     merely un-regressed.
     ---------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------
     ROUND 21 — THE VIEWING ZONE IS ONE FIELD (items A and B)

     The bundle page has continued the photograph into the caption and gallery
     bands since round 11; this card did not, so a plate sat on #F5F4F1 above a
     #F0EFEB caption above a #ECEBE7 strip — three tints under one photograph,
     which is the "three stacked bands" the owner reported. The ground is shared
     code now (paintGround in assets/nordenheim-bundle-core.js, its rules in
     assets/nordenheim-bundle-tray.css); what is here is the three things only
     this host can answer.
     ---------------------------------------------------------------------- */

  /** THE CORE'S HOOK for "where do the two ground properties live". This
   *  element: it contains the viewing zone, the deck AND this section's bottom
   *  sheet, and there is no `.nh-bb` ancestor for the builder's default to find.
   *  assets/nordenheim-plate-snap.css puts the 260ms transition on the same tag. */
  groundRoot() {
    return this;
  }

  /**
   * The stage's base image, and the ground bled behind it, set together.
   *
   * ONE writer, because they must never disagree: the overhang beside a
   * narrowed picture shows `--nh-ps-bleed`, and a stale value there is the
   * previous plate's photograph smeared down each side of the current one.
   *
   * `bleed: false` is for the pictures that are NOT cap composites — a worn or
   * editorial shot, the packaging sleeve, the plate's own artwork, and a cap
   * product's transparent PNG. Round 11 measured the same thing on the bundle
   * page: bleeding a photograph that is not a cap on a plain ground gives you a
   * blown-up crop of that photograph, which is the mat problem in a hat.
   */
  setBase(src, { bleed = true } = {}) {
    const base = this.els.base;
    if (!base) return;
    const url = `url("${cssUrl(src)}")`;
    base.style.backgroundImage = url;
    base.style.setProperty('--nh-ps-bleed', bleed ? url : 'none');
    const stage = this.els.stage;
    if (!bleed) {
      if (stage) stage.classList.remove('is-bled');
      return;
    }
    this.syncStageBleed();
  }

  /**
   * IS THE OVERHANG STILL PURE GROUND? Measured, never assumed.
   *
   * The bleed is the composite drawn at the BOX's width and the PICTURE's
   * height, so the overhang shows the square's outermost columns. Those are
   * ground only while the surround is at most 16% of the box on each side —
   * the cap itself occupies 16%..85% of the square's width. Round 11 recorded
   * the failure on the phone's compact header, where a 25% surround painted a
   * stretched ghost of the cap's own brim.
   *
   * The picture is square and fills the box's height, so its side IS the box
   * height and the fraction is height/width. 0.68 is the threshold the
   * builder's own comment states (a 16% surround each side leaves 68%).
   */
  syncStageBleed() {
    const stage = this.els.stage;
    if (!stage) return;
    const r = stage.getBoundingClientRect();
    if (!r.width || !r.height) return;
    stage.classList.toggle('is-bled', Math.min(1, r.height / r.width) >= 0.68);
  }

  /** A cap's own product photographs, as the shot shape the shared strip and
   *  the stage already speak. Empty for a colourway with no linked product. */
  capMedia(cap) {
    return (cap && Array.isArray(cap.media) ? cap.media : []).filter((m) => m && m.full);
  }

  /** THE CORE'S HOOK for "the caption is not about a plate". Returns the cap
   *  object while one is open, null otherwise — so the bundle PDPs, which
   *  never set `soloCapKey`, keep exactly the caption they had. */
  soloCap() {
    return this.soloCapKey ? this.cap(this.soloCapKey) : null;
  }

  /** THE CORE'S HOOK for "the gallery band is not this plate's". The shots ARE
   *  the cap's media, and returning them with no colourway chips is what makes
   *  rule 1 true in the strip as well as on the stage. */
  stripView() {
    const cap = this.soloCap();
    if (!cap) return null;
    const shots = this.capMedia(cap);
    if (!shots.length) return null;
    return { shots, name: cap.name || cap.key };
  }

  /**
   * Open one cap on its own.
   *
   * The colourway still moves, and it moves the ORDINARY way: by clicking the
   * real radio, so userPick, applyCap, `urlTouched`, syncUrl and haltAutoplay
   * all run exactly as they do for a swatch click. That is rule 5 — the swatch
   * row's behaviour has to survive whatever replaces it — and it is why
   * ?plate=&cap= is still written and restored from the row.
   *
   * `_soloKeep` is the one piece of bookkeeping: applyCap clears `soloCapKey`
   * (a colourway change ends a cap-only view, the same contract it has with an
   * open shot), and the radio click above lands in applyCap, so without the
   * flag this method would undo itself on its way in.
   */
  enterSoloCap(key) {
    const cap = this.cap(key);
    if (!cap) return;
    // Nothing to show is not a state worth entering: leave the panel alone
    // rather than blanking the stage for a colourway with no linked product.
    if (!this.capMedia(cap).length) return;

    this._soloKeep = true;
    try {
      const input = this.qs(`[data-nh-cap-input][value="${cssAttr(cap.key)}"]`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Already the colourway on the stage, so `change` would not fire. Run
        // the same three things its handler would have.
        this.applyCap(cap.key, { animate: false });
        this.urlTouched = true;
        this.syncUrl();
      }
    } finally {
      this._soloKeep = false;
    }
    this.haltAutoplay();

    this.soloCapKey = cap.key;
    this.shotIndex = 0;
    this.paintSoloCap();
    if (this.syncSheetAdd) this.syncSheetAdd();

    // A card tap is a request to SEE the cap, and on a phone the picture panel
    // is not in the page — it lives in a sheet. Same two branches selectPlate
    // uses, and for the same reason: exactly ONE shell is in the layout at a
    // time, the shared dock in bundle-sheet mode and this section's own bottom
    // sheet everywhere else. Without this the card would appear to do nothing
    // on the surface where the row is most useful.
    if (this.sheetActive()) {
      this.setSheet(true);
    } else if (this.els.psSheet) {
      this.revealSheet();
      this.psSetSheet(true);
    }
  }

  /** Back to the plate on the cap. `repaint: false` is for callers that paint
   *  the stage themselves a few lines later (selectPlate does). */
  exitSoloCap({ repaint = true } = {}) {
    if (!this.soloCapKey) {
      this.paintCapRow();
      return;
    }
    this.soloCapKey = null;
    this.shotIndex = -1;
    this.bcRoot().classList.remove('nh-ps--solocap');
    this.paintCapRow();
    if (this.syncSheetAdd) this.syncSheetAdd();
    if (!repaint) return;
    this.applyCap(this.capKey, { animate: false });
    // ROUND 20.1 — renderStrip, NOT the markStrip applyCap already ran.
    //
    // The two are not interchangeable here and the difference is the whole
    // second half of the bug: markStrip() only re-reads `aria-pressed` off the
    // chips that are ALREADY in the band, and coming out of a cap view those
    // are the cap's five photographs. Re-marking them leaves five chips of a
    // cap under a plate on the stage. Only renderStrip() rebuilds the row, and
    // it now reads `stripView()` as null, so it draws the plate's own shots
    // plus the eight colourways again.
    //
    // selectPlate() never needed this because syncRail() re-renders on its way
    // past; this path has no plate change to hang that off.
    if (this.els.strip && typeof this.renderStrip === 'function') {
      this.renderStrip();
      this.paintCapNote();
    }
  }

  /** Paint the panel for the open cap: stage, live region, caption band,
   *  gallery band, row. Every one of them from the same `shotIndex`. */
  paintSoloCap() {
    const cap = this.soloCap();
    const shots = this.capMedia(cap);
    const base = this.els.base;
    if (!cap || !base || !shots.length) return;

    // A CAP IS IN FRAME, so this is not the editorial state and must not be
    // mistaken for it: syncCapAddToCart withdraws "Add cap + plate" while
    // `inArtView` is true, and the whole point here is that a cap is showing.
    if (this.inEditorial) this.exitEditorial({ repaint: false });

    // Cancel a snap still in flight, or it repaints the mockup over the
    // photograph when its image lands.
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      this.pendingSrc = null;
    }
    this.swapSeq = (this.swapSeq || 0) + 1;

    this.bcRoot().classList.add('nh-ps--solocap');
    // ROUND 21, ITEM A — a cap render has no ground to continue, so the zone
    // drops to the one flat house tone. paintGround() asks soloCap() and clears
    // `is-grounded` itself; called here because applyCap() ran BEFORE
    // `soloCapKey` was set and painted the colourway's ground on the way in.
    if (this.paintGround) this.paintGround({ fade: true });

    const i = Math.max(0, Math.min(this.shotIndex, shots.length - 1));
    this.shotIndex = i;
    this.setBase(shots[i].full, { bleed: false });

    // The section's own caption over the picture is its live region — visually
    // hidden, but it is what announces the change to a screen reader, and a cap
    // card is one of the routes that has to be heard.
    const capt = this.els.caption;
    if (capt) {
      const link = capt.querySelector('a');
      if (link) {
        link.textContent = cap.name || '';
        if (cap.url) link.setAttribute('href', cap.url);
      } else {
        capt.textContent = cap.name || '';
      }
    }

    // The shared bands. paintCapNote and renderStrip both ask the two hooks
    // above what they are looking at, so neither needs a second code path here.
    if (this.els.strip && typeof this.renderStrip === 'function') this.renderStrip();
    if (typeof this.paintCapNote === 'function') this.paintCapNote();
    this.paintCapRow();
    this.syncSheetTitle();
  }

  /** One cap open, one cap bought, and the row says which is which. Cheap
   *  enough to call from every route rather than reasoning about which ones
   *  can change it. */
  paintCapRow() {
    const bought = this.boughtCapKey();
    this.qsa('[data-nh-cap-card]').forEach((btn) => {
      const key = btn.dataset.nhCapCard;
      btn.setAttribute('aria-pressed', String(key === this.soloCapKey));
      const cell = btn.closest('[data-nh-capcard-cell]');
      if (cell) cell.classList.toggle('is-viewing', key === this.soloCapKey);
      const mark = btn.querySelector('[data-nh-cap-current]');
      // `hidden`, and the stylesheet restates display:none at class strength —
      // the UA rule for [hidden] loses to any `display` on a class, which is
      // how eight cards would all have claimed the purchase.
      if (mark) mark.hidden = key !== bought;
    });
  }

  /* ------------------------------------------------- what the core asks for */

  /** Desktop: tray and button inside the panel's deck. Phone: both into the
   *  bar, which placeDock then moves into the dock's foot. */
  /**
   * ROUND 22, ITEM G — put the dock's Add button under the picture, not under
   * the accordions.
   *
   * The dock renders `.nh-bb__sheet-add` as a sibling AFTER `.nh-bb__sheet-host`,
   * and on Mix & Match the host holds the entire picture panel — so wherever the
   * panel's extras end, the button starts. Moving it in front of the extras
   * marker puts it directly below the picture, caption, strip and deck.
   *
   * Moved, never cloned, exactly like moveBuy(): the click handler is bound to
   * the element, so it travels with it, and there is only ever one button.
   * Idempotent — it compares parentNode — because psPlace() runs on every
   * matchMedia change, every debounced resize and every bundle switch.
   */
  psPlaceSheetAdd(intoPanel) {
    const btn = this.els.sheetAdd;
    if (!btn) return;
    const mark = this.extrasMark;
    if (intoPanel) {
      if (!mark || !mark.parentNode) return;
      if (btn.nextSibling !== mark) mark.parentNode.insertBefore(btn, mark);
      btn.classList.add('nh-bb__sheet-add--inpanel');
      return;
    }
    if (this.sheetAddHome && btn.parentNode !== this.sheetAddHome) {
      this.sheetAddHome.insertBefore(btn, this.sheetAddNext);
    }
    btn.classList.remove('nh-bb__sheet-add--inpanel');
  }

  moveBuy() {
    const { buy, bar, deck, trayBlock, capCombo } = this.els;
    if (!buy || !bar || !deck) return;
    // Crossing into or out of the bar with the phone's cap modal open would
    // strand the listbox in a <dialog> the other layout never opens. Same
    // first line the bundle page's moveBuy carries, for the same reason.
    if (capCombo && this.comboOpen && this.comboOpen(capCombo)) this.toggleCombo(capCombo, false);
    // sheetActive(), not "is this a phone": the bar is the DOCK'S footer and
    // nothing else. In `inline` phone mode there is no dock, so the tray and
    // the button stay in the deck inside the picture panel and travel into
    // this section's own bottom sheet with it — which is the whole of that
    // rollback. Measured before the fix: an empty 407px bar left in the page
    // flow, and 391px of horizontal scroll on a 375 screen.
    const compact = this.sheetActive();
    if (compact) {
      if (trayBlock && trayBlock.parentElement !== bar) bar.appendChild(trayBlock);
      // ROUND 19.1 — THE CAP GOES INTO THE BAR TOO, as the FIRST tile in the
      // row, exactly as the bundle page has done since round 11.
      //
      // Without this the deck stayed behind in the panel, the shared stylesheet
      // hides the deck in sheet mode, and a phone in Trio or Collector had NO
      // cap control of any kind: the swatch fieldset is display:none in card
      // mode, the ‹ › arrows and the stage are inside the parked sheet, and the
      // shopper bought whatever colourway happened to be on the picture. Starter
      // was unaffected only because its deck never leaves the panel.
      //
      // Moved, never cloned — one listbox, one set of options, one piece of
      // state. `firstElementChild`, not append: the tray head is still in the
      // DOM (visually hidden; it is the live region that announces the count)
      // and appending would put the cap after it.
      if (capCombo && trayBlock && capCombo.parentElement !== trayBlock) {
        trayBlock.insertBefore(capCombo, trayBlock.firstChild);
      }
      if (capCombo) capCombo.classList.add('nh-bb__combo--tile');
      if (buy.parentElement !== bar) bar.appendChild(buy);
      bar.hidden = false;
      bar.classList.add('nh-bb__bar--compact');
      buy.classList.add('nh-bb__buy--compact');
      if (trayBlock) trayBlock.classList.add('nh-bb__buy--compact');
    } else {
      // HOME IS AN ORDER, NOT AN APPEND. The deck holds four things in one
      // fixed sequence — tray, cap control, bundle buy block, the two plain
      // <form>s — and `appendChild` on the way back from the phone put whatever
      // was moved LAST after the plain buttons. Placed back to front (buy before
      // the plain row, the cap before buy, the tray before the cap) so a resize
      // down and up again lands byte-for-byte on the server-rendered order.
      const atcRow = deck.querySelector('.nh-ps__atc-row');
      if (buy.parentElement !== deck) deck.insertBefore(buy, atcRow || null);
      if (capCombo && capCombo.parentElement !== deck) deck.insertBefore(capCombo, buy);
      if (capCombo) capCombo.classList.remove('nh-bb__combo--tile');
      if (trayBlock && trayBlock.parentElement !== deck) {
        deck.insertBefore(trayBlock, capCombo || buy);
      }
      bar.hidden = true;
      bar.classList.remove('nh-bb__bar--compact');
      buy.classList.remove('nh-bb__buy--compact');
      if (trayBlock) trayBlock.classList.remove('nh-bb__buy--compact');
    }
    this.placeSheet();
    // The tile's accessible name is composed in JS from the button's own text
    // ("Your cap – Faluröd Suede Trucker"), which the tile hides visually; the
    // bundle page calls this here for the same reason.
    if (this.syncCapTileLabel) this.syncCapTileLabel();
    this.sizeBarTiles();
  }

  /** The core's placeDock() owns the dock's whole lifecycle; this is the one
   *  part that is per-surface. */
  placeSheet() {
    if (!this.els.dock) return;
    this.placeDock();
  }

  /**
   * WHICH NODES MOVE. This section brings its WHOLE picture panel — stage,
   * caption, plate meta, colourway swatches, editorial rail — into the dock's
   * sheet body, so the phone gets the cap, the caption and the swatches inside
   * the sheet with the shared "Add <plate>" beneath and the bar as the footer.
   * `psPlace()` is the single mover (see initSheet) and it is idempotent, so
   * this is safe to run on every resize tick.
   */
  bcSheetMove(active) {
    const { bar, sheetFoot } = this.els;
    if (this.psPlace) this.psPlace();
    if (active) {
      if (bar && sheetFoot && bar.parentElement !== sheetFoot) sheetFoot.appendChild(bar);
      return;
    }
    if (bar && this.barHome && bar.parentElement !== this.barHome) {
      this.barHome.insertBefore(bar, this.barNext);
    }
  }

  /**
   * ROUND 15.1's THIRD BUG, one file over: an inherited custom property does
   * not cross into the dock.
   *
   * applyCap writes --nh-ps-clip and the three thumb properties on THIS
   * element, and once the stage is a sibling's descendant it stops inheriting
   * them — every phone snap in bundle mode would clip at the stylesheet's
   * default. Written on the stage itself as well; on desktop that is the value
   * it inherited anyway, so it is a no-op there.
   */
  paintGeometry() {
    const stage = this.els.stage;
    if (!stage || !this.capGeom) return;
    stage.style.setProperty('--nh-ps-clip', this.capGeom.clip);
    stage.style.setProperty('--nh-ps-thumb-size', `${this.capGeom.thumb.size.toFixed(2)}%`);
    stage.style.setProperty('--nh-ps-thumb-x', `${this.capGeom.thumb.x.toFixed(2)}%`);
    stage.style.setProperty('--nh-ps-thumb-y', `${this.capGeom.thumb.y.toFixed(2)}%`);
  }

  /* The core calls these on every change; this surface has no equivalent, and
     a no-op is honest where a stub that did something would not be. */
  /* ROUND 19 — the no-op is gone. The shared gallery strip is REAL on this
     surface now, drawn by the core's renderStrip() from the same payload and
     at the same 34px chip size as the bundle page's. */
  syncCapRoving() {}
  setCompactStage() {}

  /**
   * OWNED (declared to installBundleCore).
   *
   * THE ONE PIECE OF THE SHARED PANEL THAT CANNOT BE THE SAME CODE, and why.
   *
   * The core's openShot() is three lines: set `shotIndex`, `paintStage()`,
   * repaint. It can be, because the bundle PDP's stage has ONE state — whatever
   * `currentStageSrc()` says — and a worn shot is just another source for it.
   *
   * This surface's stage does not work that way. Opening one of the plate's own
   * photographs here puts the section into an explicit EDITORIAL state
   * (`inEditorial` / `inArtView` / `.nh-ps--editorial`) which cancels a snap in
   * flight, disables the "Add cap + plate" button while no cap is in frame, and
   * swaps the caption for a label that refuses to assert a colourway nobody
   * recorded. Round 13 built all of that; `showEditorial()` is where it lives.
   * A core openShot() here would paint the photograph and leave every one of
   * those flags saying the opposite.
   *
   * So the STRIP is shared — the markup, the chips, the sizes, renderStrip()
   * and markStrip() are the core's — and only the two lines that say what a
   * chip DOES are this section's. `shotIndex` is kept in step because that is
   * what renderStrip()/markStrip()/paintCapNote() read.
   */
  openShot(i) {
    // ROUND 20 — in a cap-only view every chip in the band is one of that cap's
    // own photographs, so a chip is a picture change and nothing else: no
    // editorial state (a cap IS in frame), no colourway move, and above all no
    // change to what is bought. `shotIndex` is the same field either way, which
    // is what lets markStrip() and the pressed state stay the core's.
    if (this.soloCapKey) {
      const own = this.capMedia(this.soloCap());
      if (!own[i]) return;
      this.shotIndex = i;
      this.paintSoloCap();
      this.haltAutoplay();
      return;
    }
    const plate = this.previewedPlate();
    const shots = this.shotsFor(plate);
    const shot = shots[i];
    if (!shot) return;
    // The rail and the strip are two views of ONE list; showEditorial() indexes
    // `this.editorial`, so it has to be the list the chip was drawn from.
    this.editorial = shots;
    // ROUND 22.2 (Sina: the plate-alone view fell back to the cap mockup when
    // the next plate was clicked). The rail's radios record the shopper's
    // choice in `userPick` and syncRail() carries it to the next plate; the
    // strip's chips came through here and recorded nothing. Same rule, same
    // field: the artwork by KIND (its index differs per plate), a worn shot by
    // slot.
    this.userPick = shot.art ? { type: 'art' } : { type: 'worn', index: i };
    // Colourway first: applyCap clears the editorial state and repaints, so
    // setting the index before it would be undone a line later.
    if (shot.cap && shot.cap !== this.capKey) this.applyCap(shot.cap, { animate: false });
    this.shotIndex = i;
    this.showEditorial(i);
    this.paintCapNote();
    this.markStrip();
  }

  /** OWNED (declared to installBundleCore): the wall's radios already carry
   *  `checked`, which is what "on the stage" means here. aria-pressed on a
   *  <label> would be invalid and would say the same thing twice. */
  markPreviewed() {}

  /**
   * OWNED: on the bundle PDPs this carries the state to the OTHER rungs'
   * hrefs. Here every rung is this same section, so the state travels in the
   * address bar instead — which is also what makes a built tray shareable.
   */
  updateRungLinks() {
    this.syncUrl();
  }

  /**
   * ROUND 24 — one scroll for both reveals. Smooth, because the shopper is being
   * MOVED rather than landing somewhere they asked for by name: a jump cut from
   * the buy button to a grid of plates gives no clue that the page travelled.
   * `prefers-reduced-motion` turns it back into the instant jump this was.
   *
   * 120px of headroom clears the sticky header. The phone's dock is at the FOOT
   * of the viewport, so it never covers a target parked this high.
   */
  scrollToNode(node) {
    const top = Math.max(0, node.getBoundingClientRect().top + window.scrollY - 120);
    const smooth = !(this.reducedMotion && this.reducedMotion.matches);
    if (smooth && 'scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      window.scrollTo(0, top);
    }
    // preventScroll, or focus fights the scroll above and lands instantly.
    node.focus({ preventScroll: true });
  }

  /** What the tray's "+" and a plate-shaped nudge do: take the shopper to the
   *  plates — the tile's own Add pill is what they came for. */
  revealWall() {
    const panel = (this.panels && this.panels[this.catIndex]) || (this.panels && this.panels[0]);
    const tile = panel && (panel.querySelector('.nh-ps__thumb-cell:not([hidden]) .nh-ps__thumb'));
    if (!tile) return;
    this.scrollToNode(tile);
  }

  /** ROUND 24 — where "Add a cap for X" goes. The cap ROW (round 22's cards,
   *  each with its own Add pill), not the colourway radios under the stage:
   *  those change what the stage shows, they do not put a cap in the tray, and
   *  sending the shopper there is the one way to make the nudge a dead end.
   *
   *  The row only renders with more than one colourway and `show_cap_row` on, so
   *  fall through to the wall rather than doing nothing when it is absent. */
  revealCaps() {
    const row = this.querySelector('[data-nh-caprow]');
    const card = row && row.querySelector('[data-nh-cap-card]');
    if (!card) { this.revealWall(); return; }
    this.scrollToNode(card);
  }

  /* --------------------------------------------------------- the deep link */

  /**
   * `/?bundle=trio-bundle&cap=falurod&plates=a,b#mix-and-match`
   *
   * `cap=` and `plate=` keep their exact present meaning — `plate=` is still
   * the PREVIEWED plate on both surfaces and the picker already carries it.
   * `plates=` is the chosen tray, in order, and it is the same parameter name
   * the bundle PDP's restoreFromUrl reads, so one link works on both.
   *
   * Applied by CLICKING the tray's candidate checkbox for each handle in turn,
   * so the tray, the form inputs, the tiles' ticks and the count all land
   * through the normal path.
   */
  restoreBundleFromUrl() {
    const params = this.initialParams;
    if (!params) return;
    const wanted = (params.get('bundle') || '').trim();
    if (!wanted) return;
    // ROUND 19.1 — A STALE `?bundle=` STILL BELONGS TO THIS SECTION.
    //
    // Claimed BEFORE the handle is validated, not after. A link carrying a
    // bundle this page can no longer build (a renamed product, an old share
    // link) correctly falls back to Starter — but `bundleUrlOwned` was set only
    // on the valid path, so syncUrl() never cleaned up and the address bar kept
    // `?bundle=quad-bundle&plates=…` describing a state the page is not in.
    // Copying that link handed the next reader the same dead parameters again.
    this.bundleUrlOwned = true;
    if (!this.bundles.some((b) => b.handle === wanted)) return;
    // This section owns `bundle`/`plates` from here on, so syncUrl may clean
    // them up when the shopper leaves bundle mode.
    this.bundleUrlOwned = true;
    this.setBundle(wanted, { announce: false });
    // ROUND 18.1 — RESOLVE AND DEDUPE BEFORE TRUNCATING. `slice(0, max)` ran
    // FIRST, so a link carrying a repeat, an unknown handle or the bundle's
    // own handle spent one of the three places on nothing and a valid plate
    // further down the list was never tried — the tray came back under-filled
    // and the shopper had to work out which one was missing. Measured:
    // ?plates=cool-rat,cool-rat,be-cool-cat,boom,not-real,trio-bundle
    // restored two plates and dropped `boom`, which was perfectly valid.
    const seen = new Set();
    const list = (params.get('plates') || '').split(',')
      .map((h) => h.trim())
      .filter(Boolean)
      .filter((h) => {
        if (seen.has(h)) return false;
        seen.add(h);
        return !!this.refForHandle(h);
      })
      .slice(0, this.max);
    list.forEach((handle) => {
      const ref = this.refForHandle(handle);
      if (!ref) return;
      this.preview(ref);
      const slot = this.candidateSlot();
      if (slot < 0) return;
      const box = this.confirms.find((c) => c.dataset.nhBbConfirm === String(slot));
      if (box) box.click();
    });
  }

  refForHandle(handle) {
    const cats = (this.data && this.data.categories) || [];
    for (let ci = 0; ci < cats.length; ci += 1) {
      const plates = (cats[ci] && cats[ci].plates) || [];
      for (let pi = 0; pi < plates.length; pi += 1) {
        if (plates[pi] && plates[pi].handle === handle) return { ci, pi };
      }
    }
    return null;
  }

  /* ------------------------------------------------------------- the build */

  buildBundle() {
    const list = (this.data && this.data.bundles) || [];
    this.bundles = list.filter((b) => b && b.variants && Object.keys(b.variants).length);
    this.chosen = [];
    this.bundle = null;
    this.bundleOn = false;
    this.max = 1;
    // The cold default. Set as STATE, not just a name: setPlainRow() is not on
    // the cold path, so without these two flags the first render came up as a
    // one-slot Starter wearing the "Cap + plates" label.
    this.plainRow = 'free';
    this.freeMode = true;
    this.trayGrows = true;
    // And the classes, right away: the wall's pills, the buy block and the
    // slots' × are all keyed on `nh-ps--free`, and the first afterChange() —
    // which is what otherwise writes them — does not run until the shopper
    // touches something. Measured: the "Want more plates?" line flashed on
    // load and the buy block was hidden until the first tap.
    if (this.paintFreeTrayHead) this.paintFreeTrayHead();
    this.slotCount = Number(this.getAttribute('data-slot-count')) || 0;
    if (!this.bundles.length || !this.slotCount) return;

    // ROUND 18.1 — PROGRESSIVE ENHANCEMENT IS ONLY REAL IF THE FALLBACK
    // SURVIVES A MISSING CORE. `installBundleCore` runs at the foot of this
    // file only when `window.NordenheimBundleCore` is there; if the CDN served
    // nothing for nordenheim-bundle-core.js (CLAUDE.md records it serving
    // unexpected bytes for a quarter of an hour) the prototype has none of the
    // bundle machinery — and the pick listener a few lines down would still
    // have been bound, cancelling the click and then throwing. A dead Trio row
    // with no navigation left, which is exactly the failure the design says
    // cannot happen. Bail BEFORE binding: the picker's rows keep their own
    // href and the browser navigates to the same destination.
    if (typeof this.renderTray !== 'function' || typeof this.syncBuy !== 'function') {
      // eslint-disable-next-line no-console
      console.warn('[nordenheim-plate-snap] nordenheim-bundle-core.js is missing; '
        + 'bundle rows fall back to the bundle pages.');
      return;
    }

    this.collectBundleNodes();
    if (!this.els.button || !this.els.tray) return;

    // ROUND 19 — THE SHARED PANEL'S CONTROLS, bound by the shared code.
    //
    // `this.mq` is normally set by initSheet(); it is the breakpoint
    // toggleCombo() reads to decide between the anchored popup and the phone's
    // centred <dialog>, so it must exist even on an instance with no bottom
    // sheet of its own.
    if (!this.mq) this.mq = window.matchMedia('(max-width: 900px)');
    if (typeof this.bindCombos === 'function') this.bindCombos();
    if (typeof this.bindInfo === 'function') this.bindInfo();

    // ROUND 20 — SEED THE CAP BEING BOUGHT.
    //
    // Here rather than in build(), and that is the safety story in one line:
    // `boughtCap` exists only where a "Your cap" control exists to move it, so
    // with `bundle_inplace` off boughtCapKey() falls straight through to
    // `capKey` and every cart on the default path is what it always was.
    //
    // buildBundle() runs AFTER restoreFromUrl(), so `capKey` is already
    // whatever ?cap= asked for: a shared link buys the cap it is showing, and
    // the row's marker lands on that card. From here only selectCap() moves it.
    this.boughtCap = this.capKey;

    // The colourway the page opened on, painted onto the shared button. The cap
    // being BOUGHT, not the one on the stage — they are the same on a cold load
    // and they stop being the same the moment a cap card is clicked.
    this.paintCapButton(this.psBoughtCap());
    this.paintCapRow();

    // THE CAP ROW. A card LOOKS (rule 1): it opens that cap on its own and
    // never touches the purchased variant. Delegated and bound on `this`
    // because the row lives in the plate column, which — unlike the picture
    // panel — never travels into the dock, so one root is enough.
    //
    // The card's "See the cap" link is a SIBLING of the button, so a click on
    // it never reaches this handler; the closest() below cannot match it. The
    // explicit bail is kept anyway: it costs nothing and it states the rule at
    // the place a future nested control would break it.
    // ROUND 20.1 — LEAVING CAP VIEW IS UNCONDITIONAL ON A PLATE TAP.
    //
    // THE BUG, reproduced on live: open a cap card, then tap the plate that is
    // ALREADY selected — the stage stayed on the cap photograph and the band
    // stayed at five chips. Tapping a DIFFERENT plate was correct throughout.
    //
    // It is not an early return in selectPlate(); selectPlate has none, and it
    // is never reached. Every plate selection on this surface travels through
    // the radio's `change` event (onPsChange), and clicking a <label> whose
    // radio is already checked sets `checked` to the value it already had — so
    // the browser fires NO `change`, and nothing runs at all. Exactly the trap
    // enterSoloCap() and selectCap() each handle explicitly on the cap radios;
    // this is its plate-side twin, and it only became visible once there was a
    // view state that a plate tap had to clear even when the plate did not move.
    //
    // So the exit hangs off the CLICK, which fires either way:
    //   * plate changed  -> `change` ran first, during the input's own click
    //     dispatch, selectPlate() already called exitSoloCap({repaint:false})
    //     and painted; `soloCapKey` is null by the time this sees the click and
    //     this is a no-op. Nothing is painted twice.
    //   * plate unchanged -> no `change`, `soloCapKey` still set, and this is
    //     the only thing that runs.
    //
    // Keyboard is covered by the same handler: Space on an already-checked
    // radio runs its activation behaviour and dispatches a click without
    // changing `checked`, and the arrow keys move the selection, which fires
    // `change` and takes the first branch.
    //
    // `.nh-ps__thumb` is the <label>; the tile's "open product" arrow is a
    // SIBLING of it, so closest() cannot reach this from that link.
    this.onWallClick = (e) => {
      if (!this.soloCapKey) return;
      const tile = e.target.closest && e.target.closest('.nh-ps__thumb');
      if (!tile) return;
      this.exitSoloCap();
      this.haltAutoplay();
    };
    this.on(this, 'click', this.onWallClick);

    this.onCapRowClick = (e) => {
      if (e.target.closest && e.target.closest('[data-nh-cap-link]')) return;
      const card = e.target.closest && e.target.closest('[data-nh-cap-card]');
      if (!card) return;
      e.preventDefault();
      this.enterSoloCap(card.dataset.nhCapCard);
    };
    this.on(this, 'click', this.onCapRowClick);

    // THE GALLERY STRIP'S CHIPS. Two kinds in one row, and they do different
    // things — a colourway chip changes only what is SHOWN (round 17 item 5:
    // a look must never change the order), a photograph chip opens that shot.
    //
    // Bound as a NAMED delegated handler for the reason onPsChange is: on a
    // phone the whole picture panel travels into the dock, which is a sibling
    // of this element, and a listener on `this` never sees it. The dock's copy
    // is bound below, beside the tray's.
    this.onStripClick = (e) => {
      const capChip = e.target.closest && e.target.closest('[data-nh-bb-chip-cap]');
      if (capChip) {
        // ROUND 22.2 — a colourway chip is a pick like the cap radios: the next
        // plate opens on this colourway's mockup, not on an earlier worn slot.
        this.userPick = { type: 'cap' };
        this.applyCap(capChip.dataset.nhBbChipCap, { animate: true });
        this.urlTouched = true;
        this.syncUrl();
        this.haltAutoplay();
        return;
      }
      const shot = e.target.closest && e.target.closest('[data-nh-bb-shot]');
      if (shot) {
        this.openShot(Number(shot.dataset.nhBbShot));
        this.haltAutoplay();
      }
    };
    this.on(this, 'click', this.onStripClick);

    this.picker = this.querySelector('nordenheim-bundle-picker')
      || (this.bcRoot() && this.bcRoot().querySelector('nordenheim-bundle-picker'));

    // The picker offers to switch WITHOUT a reload. Progressive enhancement is
    // the whole shape: if this listener is not there yet (the section's own
    // IntersectionObserver has not fired) or the JS threw, nothing calls
    // preventDefault and the browser navigates to the SAME destination with a
    // reload. There is no dead link and no second behaviour to keep in step.
    this.on(document, 'nordenheim:bundle:pick', (e) => {
      const d = e.detail || {};
      if (!d.bundle) return;
      if (this.picker && d.row && !this.picker.contains(d.row)) return;
      e.preventDefault();
      if (d.bundle === 'starter' || d.bundle === 'mix' || d.bundle === 'free') {
        this.setPlainRow(d.bundle);
        this.setBundle(null);
      } else {
        // A bundle row: the plain sub-state goes back to the default, so leaving
        // the bundle again lands on "Cap + plates" rather than on whichever
        // plain row happened to be current before.
        this.setPlainRow('free');
        this.setBundle(d.bundle);
      }
      const anchor = this.settings.anchorId;
      if (anchor) {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ block: 'start' });
      }
    });

    // ROUND 18.1 — EVERY ROUTE INTO A BUNDLE ON THIS PAGE IS THE SAME ROUTE.
    //
    // The picker's rows switch in place; the "Want more plates? Trio 995 kr ·
    // Collector 1 245 kr" strip INSIDE this section and the bundle cards in
    // the band above it were still plain <a href="/products/trio-bundle">, so
    // two controls a screen apart did opposite things — one kept the cap and
    // the chosen plates, the other threw both away and reloaded. Both are
    // merchant-typed content (a richtext setting and another section), so the
    // fix is here rather than in either author's markup: any in-page link to a
    // bundle THIS section can build switches in place instead.
    //
    // A modifier click, a middle click and a target= link are left alone —
    // those mean "open the page", and the bundle PDPs stay the canonical,
    // advertisable URLs for ads and SEO.
    this.on(document, 'click', (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || (a.target && a.target !== '_self')) return;
      if (a.hasAttribute('data-nh-bp-href')) return;   // the picker owns its own rows
      const b = this.bundleForHref(a.getAttribute('href'));
      if (!b) return;
      e.preventDefault();
      // ROUND 25 (Sina, 2026-09-09, phone: "make it more like the desktop version
      // works"). A bundle link used to LOCK the tray into that bundle — "Choose 1
      // more plate", no nudge, the cap's sheet button offering a plate. The tray
      // has no modes any more (round 22): the link opens the FREE tray with the
      // shown colourway already in it, and the nudge walks the shopper to the
      // bundle they clicked ("2 more plates for 100 kr each → Trio 895 kr").
      this.setPlainRow('free');
      if (this.bundle && this.setBundle) this.setBundle(null, { announce: false });
      if (!(this.capsInTray && this.capsInTray().length)) {
        const key = this.boughtCapKey() || this.capKey;
        if (key && this.toggleCapAdd) this.toggleCapAdd(key);
      }
      const anchor = this.settings.anchorId;
      const el = anchor && document.getElementById(anchor);
      if (el) el.scrollIntoView({ block: 'start' });
    });

    if (this.els.button) {
      this.on(this.els.button, 'click', (e) => this.onAddToCart(e));
      this.on(this.els.button, 'theme:product:add', () => {
        this.announceAdd(true);
        // ROUND 22.2 (Sina: the plates left the tray after the add and the cap
        // stayed). The whole tray is in the cart now, so the whole tray
        // empties — caps included. Leaving the cap would make the next click
        // a second cap; leaving everything would make it a second order.
        this.chosen = [];
        this.capsChosen = [];
        this.capCleared = true;
        if (this.paintCapAdds) this.paintCapAdds();
        this.afterChange();
      });
      this.on(this.els.button, 'theme:product:add-error', (e) => this.announceAdd(false, e));
    }
    // The tray's checkboxes and the "+" live in the deck on desktop and in the
    // dock on a phone; the dock is OUTSIDE this element, so the delegated
    // handlers are named and bound on both roots (the core's bindSheet does
    // the second one). An event reaches exactly one of them.
    // A TAP ON THE PLATE THAT IS ALREADY ON THE STAGE still has to open the
    // sheet. Selection travels through the radio's `change`, and clicking a
    // radio that is already checked fires nothing — so on a phone the seeded
    // plate could not be added at all without first tapping a different one.
    // Idempotent with the change path: both do the same three things.
    this.on(this, 'click', (e) => {
      // ROUND 19.1 — the mirror tray follows the stage in the two plain modes
      // too, so the gate is "is the deck on screen", not "is a bundle chosen".
      if (!this.cardOn()) return;
      if (!e.target.closest('.nh-ps__thumb[data-cat]')) return;
      this.untouched = false;
      this.renderTray();
      if (this.bundleOn) this.announceCandidate();
      if (this.bundleOn && this.sheetActive()) this.setSheet(true);
    });

    this.onBundleChange = (e) => {
      const box = e.target.closest('[data-nh-bb-confirm]');
      if (box) this.onSlotToggle(box);
    };
    this.onBundleClick = (e) => {
      const plus = e.target.closest('[data-nh-bb-slot-preview]');
      if (!plus) return;
      const slot = plus.closest('[data-nh-bb-slot]');
      if (slot && slot.classList.contains('is-plus')) { this.revealWall(); return; }
      const i = Number(plus.dataset.nhBbSlotPreview);
      const ref = this.chosen[i];
      if (ref) this.preview(ref);
    };
    const host = this.els.deckHost;
    if (host) {
      this.on(host, 'change', this.onBundleChange);
      this.on(host, 'click', this.onBundleClick);
    }
    // The core's bindSheet binds these two on the DOCK, which is outside this
    // element. `change` has to carry BOTH jobs there: the tray's confirm
    // checkboxes AND the colourway swatches, which travel into the sheet with
    // the picture panel. An event reaches exactly one of the two roots, so
    // running both is not a double-handle — a swatch is not a checkbox.
    this.onDelegatedChange = (e) => {
      if (this.onPsChange) this.onPsChange(e);
      this.onBundleChange(e);
    };
    this.onDelegatedClick = (e) => {
      // ROUND 19: the dock now holds the gallery strip as well as the tray, so
      // its one click handler carries both. An event reaches exactly one root.
      if (this.onStripClick) this.onStripClick(e);
      this.onBundleClick(e);
    };

    // The dock's geometry is a function of the BAR'S measured height, and the
    // bar grows and shrinks under it: the tray's tiles are sized from its own
    // box, the pre-order line appears and goes. Measured once at placement it
    // read 77px against a settled 140 — the grip landed BELOW the bar and the
    // reserved seat was 63px short. The builder has watched its bar since round
    // 15 for the same reason; this is that observer.
    if ('ResizeObserver' in window && this.els.bar) {
      this.barRo = new ResizeObserver(() => {
        this.sizeBarTiles();
        if (this.sheetActive()) this.syncDockGeometry();
      });
      this.barRo.observe(this.els.bar);
      this.disposers.push(() => { if (this.barRo) this.barRo.disconnect(); this.barRo = null; });
    }

    // CROSSING 901px MOVES THE BUY BLOCK, and nothing else was watching for it.
    // The core's own resize net re-places the DOCK (and this section's net
    // re-places the picture panel), but which box holds the tray and the
    // button is moveBuy's decision and it ran only on a bundle switch — so a
    // 375 -> 1440 resize left both in the phone bar. Two triggers, for the
    // reason this file already records beside matchMedia: the change event was
    // observed not firing on a programmatic viewport change, and moveBuy() is
    // idempotent, so the debounced net costs a parentNode comparison.
    const bpq = this.mq || window.matchMedia('(max-width: 900px)');
    const onBreakpoint = () => { if (this.bundleOn) this.moveBuy(); };
    if (bpq.addEventListener) {
      bpq.addEventListener('change', onBreakpoint);
      this.disposers.push(() => bpq.removeEventListener('change', onBreakpoint));
    }
    let bpT = null;
    this.on(window, 'resize', () => {
      clearTimeout(bpT);
      bpT = setTimeout(onBreakpoint, 140);
    });
    this.disposers.push(() => clearTimeout(bpT));

    this.restoreBundleFromUrl();
    if (!this.bundleOn) {
      // ROUND 19 — Starter is a MODE now, not the absence of one. setPlainRow
      // has already put the section in it; this is what draws its deck: one
      // mirror slot, the cap control and "Add cap + plate".
      this.setBundle(null, { announce: false });
    }
    this.renderStrip();
    this.paintCapNote();
  }

  preload(src) {
    if (!src) return;
    this.seen = this.seen || new Set();
    if (this.seen.has(src)) return;
    this.seen.add(src);
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }

  /** Only the next plate in the cycle, not the whole category. */
  preloadNeighbour() {
    const plates = this.platesFor(this.catIndex, this.capKey);
    if (plates.length < 2) return;
    const next = plates[(this.plateIndex + 1) % plates.length];
    if (next) this.preload(stageSrc(next, this.capKey));
  }
}

/**
 * Image entries are `{stage, thumb}` per cap key — the stage wants a large
 * render and the thumbnail a small one, and requesting the right size from the
 * CDN matters at 200+ plates. A bare string is accepted too so hand-written
 * payloads stay valid.
 */
function stageSrc(plate, capKey) {
  const entry = plate && plate.images && plate.images[capKey];
  if (!entry) return '';
  return typeof entry === 'string' ? entry : entry.stage || '';
}

/**
 * Thumbnails prefer the plate's own flat artwork, which every plate product
 * already has. It is cap-independent, so those tiles never repaint when the
 * colourway changes. Only plates with no artwork fall back to a CSS crop of the
 * cap mockup, which is why the derived crop geometry still exists.
 */
function thumbSrc(plate, capKey) {
  if (plate && plate.art) return plate.art;
  const entry = plate && plate.images && plate.images[capKey];
  if (!entry) return '';
  return typeof entry === 'string' ? entry : entry.thumb || entry.stage || '';
}

/* Escape a URL for safe interpolation into a CSS url() token. */
function cssUrl(src) {
  return String(src).replace(/["\\]/g, '\\$&');
}

/* Escape a value for safe interpolation into an attribute selector. */
function cssAttr(v) {
  return String(v).replace(/["\\]/g, '\\$&');
}

// The section may appear twice on a page, which means two identical <script>
// elements; defining twice throws and aborts the rest of the file.
/* ROUND 18 — the shared half, copied onto this prototype so `this` inside
   every core method is this element. `owns` names the two the core defines and
   this section deliberately keeps its own of; anything else that would be
   clobbered is reported on the console and left alone, because a silent
   clobber of syncBuy or setSheet is a cart bug, not a styling one. */
if (typeof NH_CORE.installBundleCore === 'function') {
  NH_CORE.installBundleCore(NordenheimPlateSnap.prototype, {
    // ROUND 19 adds `openShot` and `selectCap`. Both are declared rather than
    // discovered: openShot because a chip on this surface enters the editorial
    // state the core knows nothing about (see its comment), selectCap because
    // the colourway here is a radio group and the dropdown chooses by clicking
    // the real radio rather than by owning a second copy of the state.
    // `cartClashVariantIds` was an UNDECLARED override from round 18.1: this
    // section reads the cap ids off the caps it already renders, the builder off
    // a `capVariantId` on each cap block. The behaviour was right — the host's
    // copy is the one that ran — but installBundleCore logged an error on every
    // load for it, which is noise sitting on top of the one signal that channel
    // exists to carry. Declared now.
    owns: ['markPreviewed', 'updateRungLinks', 'chosenMarkVisible',
           'cartClashVariantIds', 'openShot', 'selectCap'],
  });
}

/* ROUND 22.2 — THE SHEET'S ONE BUTTON WHILE A CAP IS OPEN.
   Sina's phone photo: the cap's solo view in the sheet read "Add Bästkusten" —
   the plate under it. The core's syncSheetAdd knows only plates; while
   soloCap() is set on the free tray the button offers the CAP and the click is
   toggleCapAdd(), with the same "Added ✓" quarter-second and park. */
{
  const P = NordenheimPlateSnap.prototype;
  const coreSync = P.syncSheetAdd;
  const coreAdd = P.onSheetAdd;
  if (typeof coreSync === 'function' && typeof coreAdd === 'function') {
    P.syncSheetAdd = function () {
      const cap = this.soloCap ? this.soloCap() : null;
      if (!cap || !this.freeMode || !this.trayItems) return coreSync.call(this);
      const btn = this.els.sheetAdd;
      if (!btn || this.sheetAdding) return;
      const name = cap.name || cap.key;
      const tpl = this.labels.addPlate;
      const text = tpl && tpl.includes('{name}')
        ? bcFill(tpl, { name })
        : `${this.labels.tileAdd || 'Add'} ${name}`;
      if (this.els.sheetAddLabel) this.els.sheetAddLabel.textContent = text;
      if (this.els.sheetAddName) this.els.sheetAddName.textContent = '';
      const full = this.trayItems().length >= this.trayCapacity();
      btn.disabled = false;
      btn.setAttribute('aria-disabled', String(full));
      btn.dataset.nhBbMode = 'cap';
    };
    P.onSheetAdd = function (e) {
      const btn = this.els.sheetAdd;
      if (!btn || btn.dataset.nhBbMode !== 'cap') return coreAdd.call(this, e);
      if (btn.getAttribute('aria-disabled') === 'true' || this.sheetAdding) return;
      const cap = this.soloCap ? this.soloCap() : null;
      if (!cap) return;
      const before = this.trayItems().length;
      // Raised BEFORE the add so afterChange's syncSheetAdd leaves the label.
      this.sheetAdding = true;
      this.toggleCapAdd(cap.key);
      if (this.trayItems().length === before) {
        this.sheetAdding = false;
        this.syncSheetAdd();
        return;
      }
      const done = this.labels.addedConfirm || 'Added ✓';
      if (this.els.sheetAddLabel) this.els.sheetAddLabel.textContent = done;
      btn.classList.add('is-added');
      btn.setAttribute('aria-disabled', 'true');
      if (this.sayStatus) this.sayStatus(`${done}, ${cap.name || ''}`);
      this.addedTimer = setTimeout(() => {
        this.timers.delete(this.addedTimer);
        this.addedTimer = null;
        this.sheetAdding = false;
        btn.classList.remove('is-added');
        this.syncSheetAdd();
        this.setSheet(false);
      }, 250);
      this.timers.add(this.addedTimer);
    };
  }
}

/* ROUND 22.2 — THE CLASH GUARD ONLY BITES WHEN TWO OFFERS WOULD REALLY MEET.
   The core refuses whenever the cart holds a bundle head or a cap. On the free
   tray that over-refused two adds that are fine: plates on their own (no offer
   travels with them — the tiers combine with anything) and a cap on top of a
   cap (the cap's free plate is PER CAP — measured 2 caps + 2 plates = 1 390,
   both free). What still clashes: a Trio/Collector against anything with an
   offer, and a cap against a bundle. */
{
  const P = NordenheimPlateSnap.prototype;
  const coreClash = P.cartClash;
  if (typeof coreClash === 'function') {
    P.cartClash = function () {
      // freeCap() recomputes `freeBundle` from the tray as it stands now.
      const fc = this.freeCap ? this.freeCap() : null;
      const addsBundle = !!(fc && this.freeBundle && this.freeBundle.handle)
        || !!(this.bundleOn && this.bundle && this.bundle.handle);
      const addsCap = !!(this.capsInTray && this.capsInTray().length) || !!(this.bundleOn && this.capChosen && this.capChosen());
      if (!addsBundle && !addsCap) return Promise.resolve(null);
      return coreClash.call(this).then((clash) => {
        if (!clash) return null;
        if (!addsBundle && clash.kind === 'cap') return null;
        return clash;
      });
    };
  }
}

if (!customElements.get(TAG)) {
  customElements.define(TAG, NordenheimPlateSnap);
}

export { NordenheimPlateSnap, derive };
